import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from "react";
import type { Actor, Permission, Scope } from "../shared/types";
import {
  ApiError,
  archive,
  auth,
  conflict,
  directory,
  files,
  finance,
  members,
  matters,
  parties as partyDirectory,
  roles,
  staff,
  stamp,
  system,
  templates,
  work,
  type ConflictHit,
  type FileRecord,
  type Matter,
  type Member,
  type Notification,
  type Party,
  type Role,
  type RoleCatalog,
  type Staff,
  type WorkItem,
  asList,
} from "./api";
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  CircleAlert,
  ClipboardCheck,
  Clock3,
  Download,
  FileArchive,
  FilePenLine,
  FilePlus2,
  FileText,
  FolderOpen,
  Home,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MoreHorizontal,
  PanelLeft,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Stamp,
  Trash2,
  Upload,
  UserCog,
  Users,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";

type Route = { name: string; id?: string; sub?: string };

const actionLabels: Record<string, string> = {
  "matter.read": "查看案件",
  "matter.create": "建立案件",
  "matter.edit": "编辑案件",
  "matter.team": "管理案件团队",
  "conflict.search": "利冲检索",
  "intake.approve": "立案审批",
  "file.read": "查看文件",
  "file.write": "上传文件",
  "stamp.create": "申请用章",
  "stamp.approve": "用章审批",
  "stamp.handle": "办理用章",
  "archive.submit": "提交归档",
  "archive.confirm": "确认归档",
  "expense.write": "登记案件经费",
  "expense.manage": "管理案件经费",
  "fee.write": "收费记录",
  "invoice.request": "申请开票",
  "invoice.confirm": "确认开票",
  "staff.read": "查看人员",
  "staff.write": "维护人员",
  "account.manage": "管理账号",
  "role.manage": "管理权限组",
  "template.manage": "管理模板",
  "system.manage": "系统管理",
};

function pathRoute(): Route {
  const raw = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  const [name = "dashboard", id, sub] = raw.split("/");
  return { name, id, sub };
}

function go(path: string) {
  window.location.hash = `#/${path.replace(/^\//, "")}`;
}

function can(actor: Actor | null, action: string, scope?: Scope) {
  return !!actor?.permissions?.some(
    (permission) =>
      permission.action === action &&
      (!scope || permission.scope === scope || permission.scope === "all"),
  );
}

function fmtDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("zh-CN");
}
function fmtDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleString("zh-CN", { hour12: false });
}
function fmtMoney(value?: number | string | null, currency = "CNY") {
  if (value === undefined || value === null || value === "") return "—";
  const number = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(number)
    ? new Intl.NumberFormat("zh-CN", { style: "currency", currency }).format(
        number / 100,
      )
    : String(value);
}
function labelStatus(value?: unknown) {
  const map: Record<string, string> = {
    draft: "草稿",
    pending: "待审批",
    pending_approval: "待审批",
    pending_partner: "待合伙人审批",
    active: "办理中",
    review_pending: "利冲待复核",
    rejected: "已拒绝",
    returned: "已退回",
    withdrawn: "已撤回",
    closed: "已结案",
    archived: "已归档",
    archive_pending: "待归档核对",
    pending_check: "待核对",
    pending_handling: "待办理",
    handled: "已办理",
    submitted: "已提交",
    confirmed: "已确认",
    completed: "已完成",
  };
  const text = String(value ?? "");
  return map[text] ?? (text || "—");
}
function matterHits(matter?: Matter) {
  if (!matter?.conflicts) return [];
  return Array.isArray(matter.conflicts)
    ? matter.conflicts
    : (matter.conflicts.hits ?? []);
}

function errorMessage(error: unknown) {
  return error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : "操作失败，请稍后重试";
}

function Button({
  children,
  variant = "primary",
  icon: Icon,
  loading,
  disabled,
  onClick,
  type = "button",
  className = "",
}: {
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "danger" | "link";
  icon?: LucideIcon;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  className?: string;
}) {
  return (
    <button
      type={type}
      className={`button button-${variant} ${className}`}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading ? (
        <RefreshCw size={16} className="spin" />
      ) : Icon ? (
        <Icon size={16} />
      ) : null}
      {children}
    </button>
  );
}
function IconButton({
  label,
  icon: Icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      className="icon-button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon size={17} />
    </button>
  );
}
function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "success" | "warning" | "danger" | "brand";
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}
function SectionTitle({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="section-title">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p className="muted">{description}</p>}
      </div>
      {actions && <div className="section-actions">{actions}</div>}
    </div>
  );
}
function Field({
  label,
  children,
  hint,
  required,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  required?: boolean;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {required && <b className="required"> *</b>}
      </span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
function ErrorBox({
  message,
  onClose,
}: {
  message: string;
  onClose?: () => void;
}) {
  return (
    <div className="error-box">
      <CircleAlert size={17} />
      <span>{message}</span>
      {onClose && <IconButton label="关闭提示" icon={X} onClick={onClose} />}
    </div>
  );
}
function SuccessBox({ children }: { children: ReactNode }) {
  return (
    <div className="success-box">
      <Check size={17} />
      <span>{children}</span>
    </div>
  );
}
function Empty({
  icon: Icon = FolderOpen,
  title,
  detail,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <Icon size={30} />
      <strong>{title}</strong>
      {detail && <span>{detail}</span>}
      {action}
    </div>
  );
}

function Login({
  onAuthenticated,
}: {
  onAuthenticated: (actor: Actor) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await auth.signIn(username.trim(), password);
      onAuthenticated(await auth.me());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  return (
    <main className="login-page">
      <div className="login-brand">
        <div className="brand-mark">狮</div>
        <div>
          <span>青狮云岸</span>
          <small>律师事务所 · OA</small>
        </div>
      </div>
      <div className="login-card">
        <div className="login-heading">
          <p className="eyebrow">LAW FIRM WORKSPACE</p>
          <h1>欢迎回来</h1>
          <p>使用律所账号登录工作台</p>
        </div>
        <form onSubmit={submit} className="stack">
          <Field label="账号" required>
            <input
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="请输入账号"
              required
            />
          </Field>
          <Field label="密码" required>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入密码"
              required
            />
          </Field>
          {error && <ErrorBox message={error} />}
          <Button type="submit" loading={loading} className="button-wide">
            登录工作台
          </Button>
        </form>
        <p className="login-note">
          账号由系统管理员创建。如忘记密码，请联系管理员重置。
        </p>
      </div>
      <footer className="login-footer">
        广东青狮云岸律师事务所 · 内部系统
      </footer>
    </main>
  );
}

function PasswordGate({
  onDone,
  onLogout,
}: {
  onDone: () => void;
  onLogout: () => void;
}) {
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (newPassword.length < 12) return setError("新密码至少需要 12 位");
    if (newPassword !== confirm) return setError("两次输入的新密码不一致");
    setLoading(true);
    try {
      await auth.changePassword(currentPassword, newPassword);
      setDone(true);
      setTimeout(onDone, 500);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  return (
    <main className="login-page">
      <div className="login-brand">
        <div className="brand-mark">狮</div>
        <div>
          <span>青狮云岸</span>
          <small>律师事务所 · OA</small>
        </div>
      </div>
      <div className="login-card">
        <div className="login-heading">
          <p className="eyebrow">首次登录</p>
          <h1>请设置新密码</h1>
          <p>为了保护律所资料，临时密码不能继续使用。</p>
        </div>
        <form onSubmit={submit} className="stack">
          <Field label="当前临时密码" required>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </Field>
          <Field
            label="新密码"
            hint="至少 12 位，建议使用字母、数字和符号组合"
            required
          >
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              required
            />
          </Field>
          <Field label="确认新密码" required>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </Field>
          {error && <ErrorBox message={error} />}
          {done && <SuccessBox>密码已更新，正在进入工作台</SuccessBox>}
          <Button
            type="submit"
            loading={loading || done}
            className="button-wide"
          >
            保存新密码
          </Button>
        </form>
        <Button variant="link" onClick={onLogout}>
          退出登录
        </Button>
      </div>
    </main>
  );
}

const navItems: {
  id: string;
  label: string;
  icon: LucideIcon;
  action?: string;
}[] = [
  { id: "dashboard", label: "工作台", icon: LayoutDashboard },
  {
    id: "new-matter",
    label: "新建立案",
    icon: FilePlus2,
    action: "matter.create",
  },
  {
    id: "matters",
    label: "案件与事项",
    icon: BriefcaseBusiness,
    action: "matter.read",
  },
  {
    id: "conflict",
    label: "利冲检索",
    icon: ShieldCheck,
    action: "conflict.search",
  },
  { id: "templates", label: "标准文书", icon: FilePenLine },
  { id: "staff", label: "人员与权限", icon: Users, action: "staff.read" },
  { id: "system", label: "系统管理", icon: Settings2, action: "system.manage" },
];

function Shell({
  actor,
  route,
  onLogout,
  children,
}: {
  actor: Actor;
  route: Route;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 760px)").matches,
  );
  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  useEffect(() => {
    setMobileMenu(false);
  }, [route.name, route.id, route.sub]);
  const visibleNav = navItems.filter(
    (item) =>
      !item.action ||
      can(actor, item.action) ||
      (item.id === "staff" &&
        (can(actor, "account.manage") || can(actor, "role.manage"))),
  );
  const active = route.name === "matter" ? "matters" : route.name;
  return (
    <div className="app-shell">
      <aside
        aria-hidden={isMobile && !mobileMenu}
        inert={isMobile && !mobileMenu ? true : undefined}
        className={`sidebar ${mobileMenu ? "sidebar-open" : ""}`}
      >
        <div className="brand">
          <div className="brand-mark">狮</div>
          <div className="brand-text">
            <strong>青狮云岸</strong>
            <span>律所工作台</span>
          </div>
          <IconButton
            label="关闭菜单"
            icon={X}
            onClick={() => setMobileMenu(false)}
          />
        </div>
        <nav className="nav-list">
          {visibleNav.map((item) => (
            <a
              href={`#/${item.id}`}
              key={item.id}
              className={`nav-item ${active === item.id ? "active" : ""}`}
              onClick={() => setMobileMenu(false)}
            >
              <item.icon size={19} />
              <span>{item.label}</span>
              {item.id === "new-matter" && (
                <span className="nav-kicker">快捷</span>
              )}
            </a>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="sidebar-tip">
            <ShieldCheck size={16} />
            <span>资料访问受权限控制</span>
          </div>
          <button className="account-mini" onClick={() => go("account")}>
            <span className="avatar">{actor.name?.slice(0, 1) || "人"}</span>
            <span>
              <strong>{actor.name}</strong>
              <small>
                {actor.identity === "partner"
                  ? "合伙人"
                  : actor.identity === "administration"
                    ? "行政财务"
                    : actor.identity === "lawyer"
                      ? "律师"
                      : "系统管理员"}
              </small>
            </span>
            <MoreHorizontal size={17} />
          </button>
        </div>
      </aside>
      {mobileMenu && (
        <button
          className="scrim"
          onClick={() => setMobileMenu(false)}
          aria-label="关闭菜单"
        />
      )}
      <div className="main-column">
        <header className="topbar">
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenu(true)}
            aria-label="打开菜单"
          >
            <Menu size={20} />
          </button>
          <div className="topbar-context">
            <span>广东青狮云岸律师事务所</span>
            <span className="dot" />{" "}
            <span>
              {route.name === "dashboard"
                ? "工作台"
                : route.name === "matter"
                  ? "案件详情"
                  : (navItems.find((i) => i.id === route.name)?.label ??
                    "工作区")}
            </span>
          </div>
          <div className="topbar-actions">
            <button
              className="notification-button"
              onClick={() => go("notifications")}
              aria-label="查看通知"
            >
              <Bell size={19} />
              <span className="notification-dot" />
            </button>
            <span className="topbar-date">
              {new Date().toLocaleDateString("zh-CN", {
                month: "long",
                day: "numeric",
                weekday: "short",
              })}
            </span>
          </div>
        </header>
        <main className="content">{children}</main>
      </div>
      <nav className="mobile-nav">
        {visibleNav.slice(0, 4).map((item) => (
          <a
            href={`#/${item.id}`}
            key={item.id}
            className={active === item.id ? "active" : ""}
          >
            <item.icon size={18} />
            <span>
              {item.label.replace("与事项", "").replace("新建", "建立")}
            </span>
          </a>
        ))}
      </nav>
    </div>
  );
}

function Dashboard({ actor }: { actor: Actor }) {
  const [items, setItems] = useState<WorkItem[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const [itemResult, notificationResult] = await Promise.allSettled([
      work.items(),
      work.notifications(),
    ]);
    if (itemResult.status === "fulfilled") setItems(itemResult.value);
    if (notificationResult.status === "fulfilled")
      setNotifications(notificationResult.value);
    const failures = [itemResult, notificationResult].filter(
      (r) => r.status === "rejected",
    );
    if (failures.length)
      setError(errorMessage((failures[0] as PromiseRejectedResult).reason));
    setRefreshedAt(new Date());
    setLoading(false);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const pending = items.filter(
    (item) =>
      !["completed", "handled", "confirmed"].includes(String(item.status)),
  ).length;
  const groups = [
    { label: "待处理流程", value: pending, icon: ListChecks, tone: "brand" },
    {
      label: "待审批立案",
      value: items.filter(
        (i) =>
          /approve|partner|intake/i.test(`${i.kind} ${i.status}`) &&
          !/returned/i.test(`${i.kind} ${i.status}`),
      ).length,
      icon: ClipboardCheck,
      tone: "warning",
    },
    {
      label: "待办理用章",
      value: items.filter((i) => /stamp/i.test(`${i.kind}`)).length,
      icon: Stamp,
      tone: "success",
    },
    {
      label: "未读通知",
      value: notifications.filter((n) => !n.read_at).length,
      icon: Bell,
      tone: "neutral",
    },
  ];
  return (
    <div className="page">
      <SectionTitle
        eyebrow="WORKSPACE"
        title="今天，从清晰开始"
        description="把需要你处理的事项放在眼前，案件资料一处维护。"
        actions={
          <Button
            variant="secondary"
            icon={RefreshCw}
            loading={loading}
            onClick={() => void load()}
          >
            刷新
            {refreshedAt && (
              <span className="button-meta">
                ·{" "}
                {refreshedAt.toLocaleTimeString("zh-CN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
          </Button>
        }
      />
      <div className="quick-actions">
        <a href="#/new-matter" className="quick-card quick-card-main">
          <span className="quick-icon">
            <Plus size={22} />
          </span>
          <span>
            <strong>新建立案</strong>
            <small>输入一次，自动完成利冲检索</small>
          </span>
          <ArrowRight size={18} />
        </a>
        <a href="#/conflict" className="quick-card">
          <span className="quick-icon soft">
            <ShieldCheck size={20} />
          </span>
          <span>
            <strong>独立利冲检索</strong>
            <small>查找全所历史主体记录</small>
          </span>
          <ArrowRight size={18} />
        </a>
        <a href="#/matters" className="quick-card">
          <span className="quick-icon soft">
            <BriefcaseBusiness size={20} />
          </span>
          <span>
            <strong>浏览案件</strong>
            <small>查看你参与的事项</small>
          </span>
          <ArrowRight size={18} />
        </a>
      </div>
      <div className="stat-grid">
        {groups.map((group) => (
          <div className="stat-card" key={group.label}>
            <div className={`stat-icon stat-${group.tone}`}>
              <group.icon size={19} />
            </div>
            <div>
              <span>{group.label}</span>
              <strong>{loading ? "…" : group.value}</strong>
            </div>
          </div>
        ))}
      </div>
      {error && <ErrorBox message={error} onClose={() => setError("")} />}
      <div className="dashboard-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">WORKFLOW</p>
              <h2>流程待办</h2>
            </div>
            <a href="#/notifications" className="text-link">
              查看全部 <ArrowRight size={15} />
            </a>
          </div>
          {loading ? (
            <LoadingRows count={4} />
          ) : items.length ? (
            <div className="work-list">
              {items.slice(0, 8).map((item) => (
                <a
                  className="work-row"
                  key={item.id}
                  href={
                    item.matter_id
                      ? `#/matter/${item.matter_id}`
                      : "#/dashboard"
                  }
                >
                  <div
                    className={`work-icon ${/approve/i.test(item.kind ?? "") ? "work-icon-warning" : ""}`}
                  >
                    {/stamp/i.test(item.kind ?? "") ? (
                      <Stamp size={17} />
                    ) : /archive/i.test(item.kind ?? "") ? (
                      <Archive size={17} />
                    ) : (
                      <ClipboardCheck size={17} />
                    )}
                  </div>
                  <div className="work-copy">
                    <strong>{item.title}</strong>
                    <span>
                      {item.detail ||
                        item.label ||
                        item.number ||
                        "打开查看业务详情"}
                    </span>
                  </div>
                  <div className="work-meta">
                    <Badge
                      tone={
                        /approve|pending|待/i.test(
                          `${item.status ?? ""} ${item.label ?? ""}`,
                        )
                          ? "warning"
                          : "neutral"
                      }
                    >
                      {item.label || labelStatus(item.status)}
                    </Badge>
                    <small>{fmtDate(item.created_at)}</small>
                  </div>
                  <ArrowRight size={16} className="row-arrow" />
                </a>
              ))}
            </div>
          ) : (
            <Empty
              icon={Check}
              title="当前没有待处理事项"
              detail="完成的业务会保留在对应案件中。"
            />
          )}
        </section>
        <section className="panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">RECENT</p>
              <h2>最近通知</h2>
            </div>
            <a href="#/notifications" className="text-link">
              全部通知 <ArrowRight size={15} />
            </a>
          </div>
          {notifications.length ? (
            <div className="notification-list">
              {notifications.slice(0, 5).map((notification) => (
                <a
                  className={`notification-row ${notification.read_at ? "" : "unread"}`}
                  key={notification.id}
                  href={
                    notification.matter_id
                      ? `#/matter/${notification.matter_id}`
                      : "#/notifications"
                  }
                >
                  <span className="notification-dot-large" />
                  <div>
                    <strong>{notification.title}</strong>
                    <span>{notification.detail}</span>
                    <small>{fmtDateTime(notification.created_at)}</small>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <Empty
              icon={Bell}
              title="还没有通知"
              detail="流程结果会在这里出现。"
            />
          )}
        </section>
      </div>
    </div>
  );
}

function LoadingRows({ count = 3 }: { count?: number }) {
  return (
    <div className="loading-rows">
      {Array.from({ length: count }, (_, index) => (
        <div className="loading-row" key={index}>
          <span />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function MattersList({ actor }: { actor: Actor }) {
  const [records, setRecords] = useState<Matter[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await matters.list({
        q,
        status,
        type,
        limit: 100,
        offset: 0,
      });
      setRecords(next);
      setOffset(next.length);
      setHasMore(next.length === 100);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [q, status, type]);
  useEffect(() => {
    void load();
  }, [load]);
  async function loadMore() {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setError("");
    try {
      const next = await matters.list({ q, status, type, limit: 100, offset });
      setRecords((current) => [...current, ...next]);
      setOffset((current) => current + next.length);
      setHasMore(next.length === 100);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  }
  return (
    <div className="page">
      <SectionTitle
        eyebrow="MATTERS"
        title="案件与事项"
        description="查看你参与的案件，或从这里继续处理已有资料。"
        actions={
          can(actor, "matter.create") && (
            <Button icon={Plus} onClick={() => go("new-matter")}>
              新建立案
            </Button>
          )
        }
      />
      <div className="filter-bar">
        <div className="search-input">
          <Search size={17} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜索案件名称、编号或当事人"
          />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">全部业务类型</option>
          <option value="litigation">诉讼</option>
          <option value="arbitration">仲裁</option>
          <option value="non_litigation">非诉</option>
          <option value="retainer">常年法律顾问</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">全部状态</option>
          <option value="draft">草稿</option>
          <option value="pending">待审批</option>
          <option value="active">办理中</option>
          <option value="review_pending">利冲待复核</option>
          <option value="archived">已归档</option>
        </select>
        <IconButton
          label="刷新案件"
          icon={RefreshCw}
          onClick={() => void load()}
        />
      </div>
      {error && <ErrorBox message={error} />}
      {loading ? (
        <LoadingRows count={6} />
      ) : records.length ? (
        <div className="panel table-panel">
          <table className="data-table">
            <thead>
              <tr>
                <th>案件</th>
                <th>业务类型</th>
                <th>承办律师</th>
                <th>状态</th>
                <th>更新日期</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {records.map((matter) => (
                <tr key={matter.id} onClick={() => go("matter/" + matter.id)}>
                  <td>
                    <div className="table-primary">
                      <span className="matter-avatar">
                        {matter.title?.slice(0, 1) || "案"}
                      </span>
                      <span>
                        <strong>{matter.title}</strong>
                        <small>{matter.number || "尚未分配案号"}</small>
                      </span>
                    </div>
                  </td>
                  <td>
                    {{
                      litigation: "诉讼",
                      arbitration: "仲裁",
                      non_litigation: "非诉",
                      retainer: "常年法律顾问",
                    }[String(matter.business_type)] ??
                      matter.business_type ??
                      "—"}
                  </td>
                  <td>{matter.lead_name || matter.lead_id || "—"}</td>
                  <td>
                    <Badge
                      tone={
                        /active|archived/i.test(matter.status ?? "")
                          ? "success"
                          : /pending|review/i.test(matter.status ?? "")
                            ? "warning"
                            : "neutral"
                      }
                    >
                      {labelStatus(matter.status)}
                    </Badge>
                  </td>
                  <td>{fmtDate(matter.updated_at)}</td>
                  <td>
                    <ArrowRight size={17} className="row-arrow" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && (
            <div className="form-actions">
              <span className="muted">已显示 {records.length} 件</span>
              <Button
                variant="secondary"
                loading={loadingMore}
                onClick={() => void loadMore()}
              >
                加载更多案件
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="panel">
          <Empty
            icon={BriefcaseBusiness}
            title="还没有案件"
            detail="从新建立案开始，案件资料会自动进入利冲数据库。"
            action={
              can(actor, "matter.create") && (
                <Button icon={Plus} onClick={() => go("new-matter")}>
                  建立第一件案件
                </Button>
              )
            }
          />
        </div>
      )}
    </div>
  );
}

type MatterForm = {
  title: string;
  business_type: string;
  lead_id: string;
  scope: string;
  fee_note: string;
  fee_amount: string;
  currency: string;
  start_date: string;
  end_date: string;
};
const blankForm: MatterForm = {
  title: "",
  business_type: "litigation",
  lead_id: "",
  scope: "",
  fee_note: "",
  fee_amount: "",
  currency: "CNY",
  start_date: "",
  end_date: "",
};

function MatterEditor({
  actor,
  initial,
  embedded = false,
  onSaved,
}: {
  actor: Actor;
  initial?: Matter;
  embedded?: boolean;
  onSaved?: (matter: Matter) => void;
}) {
  const presetNames =
    new URLSearchParams(window.location.hash.split("?")[1] ?? "")
      .get("names")
      ?.split(/\n|,|，|;|；/)
      .map((name) => name.trim())
      .filter(Boolean) ?? [];
  const [form, setForm] = useState<MatterForm>(() =>
    initial
      ? {
          title: initial.title ?? "",
          business_type: initial.business_type ?? "litigation",
          lead_id: initial.lead_id ?? "",
          scope: initial.scope ?? "",
          fee_note: initial.fee_note ?? "",
          fee_amount: initial.fee_amount
            ? String(initial.fee_amount / 100)
            : "",
          currency: initial.currency ?? "CNY",
          start_date: initial.start_date ?? "",
          end_date: initial.end_date ?? "",
        }
      : blankForm,
  );
  const [parties, setParties] = useState<Party[]>(
    initial?.parties?.length
      ? initial.parties
      : presetNames.length
        ? presetNames.map((name, index) => ({
            name,
            role: index === 0 ? "client" : "opponent",
          }))
        : [{ name: "", role: "client" }],
  );
  const [partyMatches, setPartyMatches] = useState<
    Record<number, { id: string; name: string }[]>
  >({});
  const [hits, setHits] = useState<ConflictHit[]>(matterHits(initial));
  const [conflictToken, setConflictToken] = useState("");
  const [conflictRevision, setConflictRevision] = useState<number>();
  const [saved, setSaved] = useState<Matter | undefined>(initial);
  const [directoryList, setDirectoryList] = useState<Member[]>([]);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    directory
      .list()
      .then((list) => {
        setDirectoryList(list);
        if (!initial && !form.lead_id) {
          const own = list.find((person) => person.user_id === actor.id);
          if (own) setForm((current) => ({ ...current, lead_id: own.user_id }));
        }
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (initial) {
      setSaved(initial);
      setForm({
        title: initial.title ?? "",
        business_type: initial.business_type ?? "litigation",
        lead_id: initial.lead_id ?? "",
        scope: initial.scope ?? "",
        fee_note: initial.fee_note ?? "",
        fee_amount: initial.fee_amount ? String(initial.fee_amount / 100) : "",
        currency: initial.currency ?? "CNY",
        start_date: initial.start_date ?? "",
        end_date: initial.end_date ?? "",
      });
      setParties(
        initial.parties?.length
          ? initial.parties
          : [{ name: "", role: "client" }],
      );
      setHits(matterHits(initial));
    }
  }, [initial]);
  const update = (key: keyof MatterForm, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const updateParty = (index: number, key: keyof Party, value: string) =>
    setParties((current) =>
      current.map((party, i) =>
        i === index
          ? key === "name"
            ? {
                ...party,
                name: value,
                party_id: value === party.name ? party.party_id : undefined,
                id: value === party.name ? party.id : undefined,
              }
            : { ...party, [key]: value }
          : party,
      ),
    );
  async function searchParty(index: number, query: string) {
    if (query.trim().length < 2)
      return setPartyMatches((current) => ({ ...current, [index]: [] }));
    try {
      const result = await partyDirectory.search(query.trim());
      setPartyMatches((current) => ({ ...current, [index]: result }));
    } catch {
      /* optional helper; the name can still be entered as a new主体 */
    }
  }
  function selectExistingParty(index: number, partyId: string) {
    const match = partyMatches[index]?.find((item) => item.id === partyId);
    if (!match) return;
    setParties((current) =>
      current.map((party, i) =>
        i === index
          ? { ...party, id: undefined, party_id: match.id, name: match.name }
          : party,
      ),
    );
  }
  async function renameSharedParty(index: number) {
    const party = parties[index];
    if (!party?.party_id) return;
    const nextName = window.prompt("请输入新的主体名称", party.name)?.trim();
    if (!nextName || nextName === party.name) return;
    const reason = window.prompt("请填写更名依据")?.trim();
    if (!reason) return setError("共享主体更名需要填写依据");
    try {
      await partyDirectory.rename(party.party_id, nextName, reason);
      setParties((current) =>
        current.map((item, i) =>
          i === index ? { ...item, name: nextName } : item,
        ),
      );
      setNotice("共享主体已更名，相关案件会进入利冲复核队列");
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  const savedPartySignature = saved?.parties
    ?.map((party) => `${party.party_id ?? ""}|${party.name}|${party.role}`)
    .join("||");
  const currentPartySignature = parties
    .map((party) => `${party.party_id ?? ""}|${party.name}|${party.role}`)
    .join("||");
  async function runCheck(matterId = saved?.id, forceMatter = false) {
    const names = parties.map((party) => party.name.trim()).filter(Boolean);
    if (!names.length) return setError("请先输入至少一个当事人名称");
    const checkMatterId =
      forceMatter || currentPartySignature === savedPartySignature
        ? matterId
        : undefined;
    setChecking(true);
    setError("");
    try {
      const result = await conflict.check(names, checkMatterId);
      setHits(result.hits ?? []);
      setConflictToken(result.token);
      setConflictRevision(result.revision);
      if (!checkMatterId && saved?.id)
        setNotice("已按当前输入检查利冲；保存后会把结果绑定到案件");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setChecking(false);
    }
  }
  async function save(event?: FormEvent, submit = false) {
    event?.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    const payload = {
      ...form,
      revision: saved?.revision,
      fee_amount: form.fee_amount
        ? Math.round(Number(form.fee_amount) * 100)
        : 0,
      parties: parties
        .filter((party) => party.name.trim())
        .map((party) => ({ ...party, name: party.name.trim() })),
    };
    try {
      const result = saved
        ? await matters.update(saved.id, payload)
        : await matters.create(payload);
      setSaved(result);
      setNotice("案件资料已保存，正在刷新利冲结果");
      await runCheck(result.id, true);
      onSaved?.(result);
      if (submit) {
        await matters.submit(result.id);
        const submitted = await matters.get(result.id);
        setSaved(submitted);
        setNotice("已提交合伙人审批");
        onSaved?.(submitted);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  const disabled = !can(actor, saved ? "matter.edit" : "matter.create");
  return (
    <form
      className={`matter-form ${embedded ? "" : "page"}`}
      onSubmit={(event) => void save(event)}
      noValidate
    >
      <SectionTitle
        eyebrow={embedded ? "OVERVIEW" : "NEW MATTER"}
        title={embedded ? "案件资料" : "新建立案"}
        description={
          embedded
            ? "修改当事人或业务信息后，系统会重新进行利冲检查。"
            : "一次填写案件基本资料，保存后自动进入利冲数据库。"
        }
        actions={
          embedded ? (
            <div className="inline-actions">
              <Button
                variant="secondary"
                icon={RefreshCw}
                loading={checking}
                onClick={() => void runCheck()}
              >
                重新检查利冲
              </Button>
              <Button type="submit" loading={busy} disabled={disabled}>
                保存资料
              </Button>
            </div>
          ) : (
            <Button type="submit" loading={busy} disabled={disabled}>
              保存并检查利冲
            </Button>
          )
        }
      />
      {error && <ErrorBox message={error} onClose={() => setError("")} />}
      {notice && <SuccessBox>{notice}</SuccessBox>}
      <div className="form-layout">
        <div className="form-main">
          <section className="panel form-section">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">01 · 基本资料</p>
                <h2>案件信息</h2>
              </div>
            </div>
            <div className="form-grid">
              <Field label="案件名称" required>
                <input
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  placeholder="例如：某某公司股权纠纷案"
                  required
                  disabled={disabled}
                />
              </Field>
              <Field label="业务类型" required>
                <select
                  value={form.business_type}
                  onChange={(e) => update("business_type", e.target.value)}
                  disabled={disabled}
                >
                  <option value="litigation">诉讼</option>
                  <option value="arbitration">仲裁</option>
                  <option value="non_litigation">非诉</option>
                  <option value="retainer">常年法律顾问</option>
                </select>
              </Field>
              <Field label="承办律师" required>
                <select
                  value={form.lead_id}
                  onChange={(e) => update("lead_id", e.target.value)}
                  disabled={disabled}
                  required
                >
                  <option value="">选择主要承办律师</option>
                  {directoryList.map((person) => (
                    <option key={person.user_id} value={person.user_id}>
                      {person.name} ·{" "}
                      {person.identity === "partner" ? "合伙人" : "律师"}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="委托范围" required>
                <textarea
                  rows={3}
                  value={form.scope}
                  onChange={(e) => update("scope", e.target.value)}
                  placeholder="简要说明本次委托事项和服务范围"
                  required
                  disabled={disabled}
                />
              </Field>
              <Field label="收费约定" required>
                <textarea
                  rows={3}
                  value={form.fee_note}
                  onChange={(e) => update("fee_note", e.target.value)}
                  placeholder="收费方式、付款节点或其他约定"
                  required
                  disabled={disabled}
                />
              </Field>
              <div className="form-grid compact">
                <Field label="约定金额">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.fee_amount}
                    onChange={(e) => update("fee_amount", e.target.value)}
                    placeholder="0.00"
                    disabled={disabled}
                  />
                </Field>
                <Field label="币种">
                  <select
                    value={form.currency}
                    onChange={(e) => update("currency", e.target.value)}
                    disabled={disabled}
                  >
                    <option value="CNY">人民币 CNY</option>
                  </select>
                </Field>
              </div>
              <div className="form-grid compact">
                <Field label="委托开始">
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => update("start_date", e.target.value)}
                    disabled={disabled}
                  />
                </Field>
                <Field label="委托结束">
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => update("end_date", e.target.value)}
                    disabled={disabled}
                  ></input>
                </Field>
              </div>
            </div>
          </section>
          <section className="panel form-section">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">02 · 利冲主体</p>
                <h2>各方当事人</h2>
                <p className="muted">
                  可选择已有主体以保留历史合作关系；新名称会创建为新的主体记录。
                </p>
              </div>
              <Button
                variant="secondary"
                icon={Plus}
                onClick={() =>
                  setParties((current) => [
                    ...current,
                    { name: "", role: "opponent" },
                  ])
                }
                disabled={disabled}
              >
                添加一方
              </Button>
            </div>
            <div className="party-list">
              {parties.map((party, index) => (
                <div
                  className="party-row"
                  key={`${party.id ?? party.party_id ?? "new"}-${index}`}
                >
                  <span className="party-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="party-input-group">
                    <input
                      value={party.name}
                      onChange={(e) => {
                        updateParty(index, "name", e.target.value);
                        void searchParty(index, e.target.value);
                      }}
                      placeholder="完整主体名称"
                      required={index === 0}
                      disabled={disabled}
                    />
                    {partyMatches[index]?.length ? (
                      <select
                        aria-label="选择已有主体"
                        value={party.party_id ?? ""}
                        onChange={(e) =>
                          selectExistingParty(index, e.target.value)
                        }
                        disabled={disabled}
                      >
                        <option value="">选择已有主体（可选）</option>
                        {partyMatches[index].map((match) => (
                          <option key={match.id} value={match.id}>
                            {match.name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {party.party_id && (
                      <>
                        <small className="party-linked">
                          已引用主体记录，修改名称会解除引用并创建新主体
                        </small>
                        {(can(actor, "matter.edit", "all") ||
                          can(actor, "staff.write", "all")) && (
                          <Button
                            variant="link"
                            className="party-rename"
                            onClick={() => void renameSharedParty(index)}
                          >
                            维护共享主体名称
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  <select
                    value={party.role}
                    onChange={(e) => updateParty(index, "role", e.target.value)}
                    disabled={disabled}
                  >
                    <option value="client">委托人</option>
                    <option value="opponent">对方当事人</option>
                    <option value="third_party">关联方</option>
                    <option value="related">其他方</option>
                  </select>
                  {parties.length > 1 && (
                    <IconButton
                      label="删除当事人"
                      icon={Trash2}
                      onClick={() =>
                        setParties((current) =>
                          current.filter((_, i) => i !== index),
                        )
                      }
                      disabled={disabled}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="conflict-strip">
              <div className="conflict-strip-heading">
                <div className="quick-icon soft">
                  <ShieldCheck size={19} />
                </div>
                <div>
                  <strong>利冲检查结果</strong>
                  <span>
                    {checking
                      ? "正在检查全所主体记录…"
                      : hits.length
                        ? `发现 ${hits.length} 条需要关注的记录`
                        : "尚未发现命中记录，仍需在提交和审批前复核"}
                  </span>
                </div>
              </div>
              <Button
                variant="secondary"
                loading={checking}
                onClick={() => void runCheck()}
                disabled={disabled}
              >
                立即检查
              </Button>
            </div>
            {hits.length > 0 && (
              <div className="hit-list">
                {hits.map((hit, index) => (
                  <ConflictHitRow
                    hit={hit}
                    key={hit.id ?? `${hit.matter_id}-${index}`}
                  />
                ))}
              </div>
            )}
          </section>
          {saved && !embedded && (
            <div className="form-footer-actions">
              <Button
                variant="secondary"
                onClick={() => go(`matter/${saved.id}`)}
              >
                查看案件
              </Button>
              {["draft", "returned", "review_pending"].includes(
                String(saved.status),
              ) && (
                <Button
                  icon={Check}
                  onClick={() => void save(undefined, true)}
                  loading={busy}
                >
                  提交合伙人审批
                </Button>
              )}
            </div>
          )}
        </div>
        <aside className="form-aside">
          <div className="aside-card accent">
            <ShieldCheck size={22} />
            <strong>利冲和立案在同一处完成</strong>
            <p>
              案件第一次保存就进入全所利冲数据库。修改当事人后会自动重新检查。
            </p>
          </div>
          <div className="aside-card">
            <p className="eyebrow">SUBMISSION</p>
            <h3>提交前检查</h3>
            <ul className="check-list">
              <li className={form.title ? "done" : ""}>
                <Check size={15} />
                案件名称
              </li>
              <li className={form.business_type ? "done" : ""}>
                <Check size={15} />
                业务类型
              </li>
              <li className={parties.some((p) => p.name.trim()) ? "done" : ""}>
                <Check size={15} />
                至少一方当事人
              </li>
              <li className={form.scope ? "done" : ""}>
                <Check size={15} />
                委托范围
              </li>
              <li className={form.fee_note || form.fee_amount ? "done" : ""}>
                <Check size={15} />
                收费约定
              </li>
            </ul>
          </div>
          {saved && (
            <div className="aside-card">
              <p className="eyebrow">MATTER STATUS</p>
              <h3>{labelStatus(saved.status)}</h3>
              <p className="muted">
                最后更新于 {fmtDateTime(saved.updated_at)}
              </p>
            </div>
          )}
        </aside>
      </div>
    </form>
  );
}

function ConflictHitRow({ hit }: { hit: ConflictHit }) {
  const roleLabels: Record<string, string> = {
    client: "委托人",
    opponent: "对方当事人",
    third_party: "第三方",
    related: "关联方",
  };
  const history = hit.classification === "history";
  const matterNumber = hit.number || hit.matter_number;
  const matterTitle = hit.title || hit.matter_title;
  const status = hit.status || hit.matter_status;
  const details = [
    roleLabels[String(hit.role ?? "")] || hit.role,
    matterNumber || matterTitle,
    status ? labelStatus(status) : "",
    hit.lead_name ? `承办：${hit.lead_name}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className={`hit-row ${history ? "hit-row-history" : ""}`}>
      <div className="hit-marker">
        {history ? <Check size={17} /> : <CircleAlert size={17} />}
      </div>
      <div className="hit-main">
        <strong>{hit.party_name || hit.name || "命中主体"}</strong>
        <span>{details || "来源案件记录"}</span>
        {history && <small>与历史案件中的同一委托人合作记录，仅作提示</small>}
      </div>
      <Badge tone={history ? "success" : "warning"}>
        {history ? "历史合作提示" : "需要关注"}
      </Badge>
    </div>
  );
}

function ConflictPage() {
  const [names, setNames] = useState("");
  const [hits, setHits] = useState<ConflictHit[]>([]);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    const list = names
      .split(/[\n,，;；]/)
      .map((name) => name.trim())
      .filter(Boolean);
    if (!list.length) return setError("请输入至少一个主体名称");
    setLoading(true);
    setError("");
    try {
      const result = await conflict.check(list);
      setHits(result.hits ?? []);
      setToken(result.token);
      setSearched(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  function startMatter() {
    const encoded = encodeURIComponent(names.trim());
    go(`new-matter?names=${encoded}`);
  }
  return (
    <div className="page">
      <SectionTitle
        eyebrow="CONFLICT CHECK"
        title="利冲检索"
        description="搜索全所已登记案件中的各方当事人名称，结果只用于提示，不能单凭名称判断主体同一。"
        actions={
          <Button
            variant="secondary"
            icon={RefreshCw}
            onClick={() => {
              setNames("");
              setHits([]);
              setSearched(false);
              setError("");
            }}
          >
            清空
          </Button>
        }
      />
      <div className="conflict-layout">
        <section className="panel conflict-search-card">
          <div className="conflict-intro">
            <div className="quick-icon">
              <ShieldCheck size={23} />
            </div>
            <div>
              <h2>输入主体名称</h2>
              <p>
                支持多个名称，每行一个。精确匹配优先，同时保留关键词包含命中。
              </p>
            </div>
          </div>
          <form onSubmit={submit}>
            <Field label="待检索名称" required>
              <textarea
                rows={6}
                value={names}
                onChange={(e) => setNames(e.target.value)}
                placeholder={"例如：\n广东某某科技有限公司\n张三"}
                required
              />
            </Field>
            {error && <ErrorBox message={error} />}
            <div className="form-actions">
              <span className="muted">检索仅读取必要的案件提示字段</span>
              <Button type="submit" icon={Search} loading={loading}>
                开始检索
              </Button>
            </div>
          </form>
        </section>
        <section className="panel conflict-results">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">RESULTS</p>
              <h2>检索结果</h2>
            </div>
            {searched && (
              <Badge tone={hits.length ? "warning" : "success"}>
                {hits.length ? `${hits.length} 条命中` : "未发现命中"}
              </Badge>
            )}
          </div>
          {!searched ? (
            <Empty
              icon={Search}
              title="等待检索"
              detail="输入名称后，结果会显示在这里。"
            />
          ) : hits.length ? (
            <>
              <div className="hit-list">
                {hits.map((hit, index) => (
                  <ConflictHitRow
                    hit={hit}
                    key={hit.id ?? `${hit.matter_id}-${index}`}
                  />
                ))}
              </div>
              <div className="form-actions">
                <span className="muted">
                  命中仅作提示，立案时仍需由合伙人判断。
                </span>
                <Button onClick={startMatter}>带入名称并立案</Button>
              </div>
            </>
          ) : (
            <div className="result-success">
              <div className="success-round">
                <Check size={25} />
              </div>
              <strong>未发现命中记录</strong>
              <span>
                这只是当前数据的检索结果。正式立案仍需在提交和审批前复核。
              </span>
              <Button onClick={startMatter}>带入名称并立案</Button>
            </div>
          )}
          {token && (
            <p className="result-footnote">
              本次检索已生成校验凭据，提交审批时系统会再次复核最新数据。
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

function MatterDetail({ actor, matterId }: { actor: Actor; matterId: string }) {
  const [matter, setMatter] = useState<Matter>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(pathRoute().sub || "overview");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setMatter(await matters.get(matterId));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [matterId]);
  useEffect(() => {
    void load();
  }, [load]);
  function changeTab(next: string) {
    setTab(next);
    window.history.replaceState(null, "", `#/matter/${matterId}/${next}`);
  }
  if (loading)
    return (
      <div className="page">
        <LoadingRows count={5} />
      </div>
    );
  if (error || !matter)
    return (
      <div className="page">
        <Button variant="ghost" icon={ArrowLeft} onClick={() => go("matters")}>
          返回案件
        </Button>
        <ErrorBox message={error || "案件不存在"} />
      </div>
    );
  const tabs = [
    { id: "overview", label: "概览", icon: FileText },
    { id: "team", label: "案件团队", icon: Users },
    { id: "files", label: "文件与历史", icon: FolderOpen },
    { id: "stamp", label: "用章与出函", icon: Stamp },
    { id: "finance", label: "收费与经费", icon: BriefcaseBusiness },
    { id: "archive", label: "归档", icon: Archive },
  ];
  return (
    <div className="page">
      <button className="back-link" onClick={() => go("matters")}>
        <ArrowLeft size={16} />
        返回案件列表
      </button>
      <div className="matter-head">
        <div className="matter-head-main">
          <span className="matter-avatar large">
            {matter.title?.slice(0, 1) || "案"}
          </span>
          <div>
            <p className="eyebrow">{matter.number || "登记编号"}</p>
            <h1>{matter.title}</h1>
            <div className="matter-sub">
              <Badge
                tone={
                  /approved|active|archived/i.test(matter.status ?? "")
                    ? "success"
                    : /pending/i.test(matter.status ?? "")
                      ? "warning"
                      : "neutral"
                }
              >
                {labelStatus(matter.status)}
              </Badge>
              <span>{matter.business_type || "—"}</span>
              <span>
                承办：{matter.lead_name || matter.lead_id || "未指定"}
              </span>
            </div>
          </div>
        </div>
        <div className="matter-head-actions">
          {can(actor, "matter.create") && (
            <Button
              variant="secondary"
              icon={FilePenLine}
              onClick={() => go(`templates?matter=${matter.id}`)}
            >
              生成文书
            </Button>
          )}
          {(matter.status === "draft" || matter.status === "returned") &&
            can(actor, "matter.edit") && (
              <Button
                icon={Check}
                onClick={async () => {
                  try {
                    await matters.submit(matter.id);
                    await load();
                  } catch (e) {
                    setError(errorMessage(e));
                  }
                }}
              >
                提交审批
              </Button>
            )}
        </div>
      </div>
      <nav className="tabs">
        {tabs.map((item) => (
          <button
            key={item.id}
            className={tab === item.id ? "active" : ""}
            onClick={() => changeTab(item.id)}
          >
            <item.icon size={17} />
            {item.label}
          </button>
        ))}
      </nav>
      {error && <ErrorBox message={error} onClose={() => setError("")} />}
      {tab === "overview" && (
        <div className="detail-grid">
          <div>
            <MatterEditor
              actor={actor}
              initial={matter}
              embedded
              onSaved={(next) => {
                setMatter((current) => ({ ...current, ...next }));
              }}
            />
            <ApprovalPanel actor={actor} matter={matter} onDone={load} />
            <DecisionHistory matter={matter} />
          </div>
          <MatterSummary matter={matter} />
        </div>
      )}
      {tab === "team" && (
        <TeamTab actor={actor} matter={matter} onChange={load} />
      )}
      {tab === "files" && <FilesTab actor={actor} matter={matter} />}
      {tab === "stamp" && <StampTab actor={actor} matter={matter} />}
      {tab === "finance" && <FinanceTab actor={actor} matter={matter} />}
      {tab === "archive" && <ArchiveTab actor={actor} matter={matter} />}
    </div>
  );
}

function MatterSummary({ matter }: { matter: Matter }) {
  return (
    <aside className="detail-side">
      <div className="aside-card">
        <p className="eyebrow">MATTER SNAPSHOT</p>
        <h3>案件摘要</h3>
        <dl className="summary-list">
          <div>
            <dt>委托范围</dt>
            <dd>{matter.scope || "—"}</dd>
          </div>
          <div>
            <dt>收费约定</dt>
            <dd>
              {matter.fee_note || fmtMoney(matter.fee_amount, matter.currency)}
            </dd>
          </div>
          <div>
            <dt>开始日期</dt>
            <dd>{fmtDate(matter.start_date)}</dd>
          </div>
          <div>
            <dt>最后更新</dt>
            <dd>{fmtDateTime(matter.updated_at)}</dd>
          </div>
        </dl>
      </div>
      <div className="aside-card">
        <div className="panel-heading">
          <h3>当事人</h3>
          <Badge>{matter.parties?.length ?? 0}</Badge>
        </div>
        <div className="party-summary">
          {matter.parties?.map((party, index) => (
            <div key={party.id ?? index}>
              <span>{party.name}</span>
              <small>{party.role}</small>
            </div>
          )) || <span className="muted">暂无当事人</span>}
        </div>
      </div>
    </aside>
  );
}

function DecisionHistory({ matter }: { matter: Matter }) {
  const decisions = matter.decisions ?? [];
  if (!decisions.length) return null;
  return (
    <section className="panel decision-history">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">APPROVAL HISTORY</p>
          <h2>审批记录</h2>
          <p className="muted">
            每次立案决定都会保留审批意见、利冲处理依据和关联文件。
          </p>
        </div>
        <Badge>{decisions.length} 次</Badge>
      </div>
      <div className="decision-list">
        {decisions.map((decision, index) => {
          const snapshot =
            decision.snapshot && typeof decision.snapshot === "object"
              ? (decision.snapshot as Record<string, unknown>)
              : {};
          const resolutions = Array.isArray(snapshot.resolutions)
            ? (snapshot.resolutions as Record<string, unknown>[])
            : [];
          return (
            <article
              className="decision-row"
              key={String(decision.id ?? index)}
            >
              <div className="decision-heading">
                <strong>{labelStatus(String(decision.outcome ?? ""))}</strong>
                <span>
                  {String(decision.actor_name ?? decision.actor_id ?? "合伙人")}{" "}
                  · {fmtDateTime(String(decision.created_at ?? ""))}
                </span>
              </div>
              {String(decision.reason ?? "").trim() && (
                <p>{String(decision.reason)}</p>
              )}
              {resolutions.length > 0 && (
                <div className="decision-resolutions">
                  {resolutions.map((resolution, resolutionIndex) => (
                    <div key={String(resolution.hit_id ?? resolutionIndex)}>
                      <span>
                        {resolution.type === "false_positive"
                          ? "误报排除"
                          : resolution.type === "waiver"
                            ? "当事人利冲豁免"
                            : "不可豁免冲突"}
                      </span>
                      <small>{String(resolution.reason ?? "")}</small>
                      {String(resolution.file_id ?? "") && (
                        <a href={files.contentUrl(String(resolution.file_id))}>
                          查看依据文件
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ApprovalPanel({
  actor,
  matter,
  onDone,
}: {
  actor: Actor;
  matter: Matter;
  onDone: () => void;
}) {
  const mayApprove =
    can(actor, "intake.approve", "all") &&
    actor.identity === "partner" &&
    matter.created_by !== actor.id &&
    ["pending", "pending_approval", "pending_partner"].includes(
      String(matter.status),
    );
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<"approve" | "return" | "reject">(
    "approve",
  );
  const [resolutions, setResolutions] = useState<
    Record<
      string,
      {
        type: "false_positive" | "waiver" | "prohibited";
        reason: string;
        file_id?: string;
      }
    >
  >({});
  const [matterFiles, setMatterFiles] = useState<FileRecord[]>([]);
  useEffect(() => {
    if (mayApprove)
      files
        .list(matter.id)
        .then((items) =>
          setMatterFiles(
            items.filter((file) => file.status === "ready" || !file.status),
          ),
        )
        .catch(() => undefined);
  }, [matter.id, mayApprove]);
  if (!mayApprove) return null;
  const hits = matterHits(matter);
  const conflictToken =
    matter.conflicts && !Array.isArray(matter.conflicts)
      ? matter.conflicts.token
      : undefined;
  function updateResolution(
    hit: ConflictHit,
    patch: Partial<{
      type: "false_positive" | "waiver" | "prohibited";
      reason: string;
      file_id?: string;
    }>,
  ) {
    const id = String(hit.hit_id ?? hit.id ?? "");
    setResolutions((current) => ({
      ...current,
      [id]: {
        type: current[id]?.type ?? "false_positive",
        reason: current[id]?.reason ?? "",
        ...patch,
      },
    }));
  }
  async function decide() {
    if (outcome !== "approve" && !reason.trim())
      return setError("退回或拒绝需要填写处理理由");
    if (outcome === "approve") {
      const reviewHits = hits.filter((hit) => hit.classification !== "history");
      const unresolved = reviewHits.find(
        (hit) => !resolutions[String(hit.hit_id ?? hit.id)]?.reason.trim(),
      );
      if (unresolved) return setError("请逐项填写利冲命中的处理依据");
      if (
        reviewHits.some(
          (hit) =>
            resolutions[String(hit.hit_id ?? hit.id)]?.type === "waiver" &&
            !resolutions[String(hit.hit_id ?? hit.id)]?.file_id,
        )
      )
        return setError("利冲豁免需要选择已上传的书面材料");
    }
    setLoading(true);
    setError("");
    try {
      await matters.decision(matter.id, {
        outcome,
        reason,
        token: conflictToken,
        resolutions: Object.entries(resolutions).map(
          ([hit_id, resolution]) => ({ hit_id, ...resolution }),
        ),
      });
      await onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  return (
    <section className="panel approval-panel">
      <div className="approval-top">
        <div className="quick-icon soft">
          <ClipboardCheck size={20} />
        </div>
        <div>
          <p className="eyebrow">PARTNER REVIEW</p>
          <h2>待合伙人审批</h2>
          <p>你可以查看全所待审批立案，并处理这件案件。</p>
        </div>
        <Button onClick={() => setOpen((v) => !v)}>
          {open ? "收起审批" : "打开审批"}
        </Button>
      </div>
      {open && (
        <div className="approval-body">
          {hits.length ? (
            <div className="warning-callout">
              <CircleAlert size={18} />
              <div>
                <strong>存在利冲命中</strong>
                <span>请核对命中主体。误报或当事人豁免需要保留处理依据。</span>
              </div>
            </div>
          ) : (
            <div className="success-box">当前没有已记录的利冲命中。</div>
          )}
          {hits
            .filter((hit) => hit.classification !== "history")
            .map((hit, index) => {
              const key = String(hit.hit_id ?? hit.id ?? index);
              const current = resolutions[key] ?? {
                type: "false_positive" as const,
                reason: "",
              };
              return (
                <div className="resolution-row" key={key}>
                  <ConflictHitRow hit={hit} />
                  <div className="resolution-fields">
                    <select
                      value={current.type}
                      onChange={(event) =>
                        updateResolution(hit, {
                          type: event.target.value as
                            | "false_positive"
                            | "waiver"
                            | "prohibited",
                        })
                      }
                    >
                      <option value="false_positive">误报／主体并非同一</option>
                      <option value="waiver">当事人利冲豁免</option>
                      <option value="prohibited">不可豁免冲突</option>
                    </select>
                    <input
                      value={current.reason}
                      onChange={(event) =>
                        updateResolution(hit, { reason: event.target.value })
                      }
                      placeholder="填写处理依据"
                    />
                    {current.type === "waiver" && (
                      <select
                        value={current.file_id ?? ""}
                        onChange={(event) =>
                          updateResolution(hit, { file_id: event.target.value })
                        }
                        required
                      >
                        <option value="">选择已上传的豁免材料</option>
                        {matterFiles.map((file) => (
                          <option key={file.id} value={file.id}>
                            {file.name || file.file_name}
                            {file.revision ? ` · 第 ${file.revision} 版` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              );
            })}
          <div className="segmented">
            <button
              className={outcome === "approve" ? "selected" : ""}
              onClick={() => setOutcome("approve")}
            >
              通过
            </button>
            <button
              className={outcome === "return" ? "selected" : ""}
              onClick={() => setOutcome("return")}
            >
              退回补充
            </button>
            <button
              className={outcome === "reject" ? "selected danger" : ""}
              onClick={() => setOutcome("reject")}
            >
              拒绝承接
            </button>
          </div>
          {outcome !== "approve" || hits.length ? (
            <Field label="审批意见" required={outcome !== "approve"}>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  outcome === "approve"
                    ? "可记录本次审批的补充说明"
                    : "请说明处理依据"
                }
              />
            </Field>
          ) : null}
          {error && <ErrorBox message={error} />}
          <div className="form-actions">
            <span className="muted">审批提交前会按最新资料重新检查利冲</span>
            <Button
              loading={loading}
              icon={Check}
              onClick={() => void decide()}
            >
              确认
              {outcome === "approve"
                ? "通过"
                : outcome === "return"
                  ? "退回"
                  : "拒绝"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function TeamTab({
  actor,
  matter,
  onChange,
}: {
  actor: Actor;
  matter: Matter;
  onChange: () => void;
}) {
  const [team, setTeam] = useState<Member[]>(matter.members ?? []);
  const [directoryList, setDirectoryList] = useState<Member[]>([]);
  const [userId, setUserId] = useState("");
  const [leadId, setLeadId] = useState(matter.lead_id ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const canEdit =
    can(actor, "matter.team", "participating") ||
    can(actor, "matter.team", "all");
  useEffect(() => {
    if (canEdit)
      directory
        .list()
        .then(setDirectoryList)
        .catch((e) => setError(errorMessage(e)));
  }, [canEdit]);
  async function add() {
    const selected = directoryList.find((person) => person.user_id === userId);
    if (!selected) return setError("请选择一名在职律师");
    setLoading(true);
    setError("");
    try {
      await members.add(matter.id, { user_id: selected.user_id });
      setUserId("");
      setTeam((current) => [...current, selected]);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  async function remove(userId: string) {
    setLoading(true);
    setError("");
    try {
      await members.remove(matter.id, userId);
      setTeam((current) =>
        current.filter((member) => member.user_id !== userId),
      );
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  async function handover() {
    if (!leadId || leadId === matter.lead_id) return;
    setLoading(true);
    setError("");
    try {
      await matters.setLead(matter.id, leadId);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  return (
    <div className="detail-grid">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">MATTER TEAM</p>
            <h2>参与律师</h2>
            <p className="muted">
              团队成员决定谁可以访问案件完整资料；交接会保留原承办历史并通知接手人。
            </p>
          </div>
          <Badge tone="brand">{team.length} 人</Badge>
        </div>
        {error && <ErrorBox message={error} />}
        {team.length ? (
          <div className="member-list">
            {team.map((member) => (
              <div className="member-row" key={member.user_id}>
                <span className="avatar">
                  {member.name?.slice(0, 1) || "人"}
                </span>
                <div>
                  <strong>
                    {member.name}
                    {member.user_id === matter.lead_id && (
                      <Badge tone="brand">主要承办</Badge>
                    )}
                  </strong>
                  <span>
                    {member.identity === "partner"
                      ? "合伙人"
                      : member.identity === "administration"
                        ? "行政财务"
                        : member.role || "律师"}
                  </span>
                </div>
                {canEdit && member.user_id !== matter.lead_id && (
                  <IconButton
                    label="移除参与律师"
                    icon={X}
                    onClick={() => void remove(member.user_id)}
                    disabled={loading}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <Empty
            icon={Users}
            title="还没有参与律师"
            detail="添加团队成员后，他们才能访问案件完整资料。"
          />
        )}
        {canEdit && (
          <>
            <div className="inline-form">
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              >
                <option value="">选择在职律师</option>
                {directoryList
                  .filter(
                    (person) =>
                      !team.some((member) => member.user_id === person.user_id),
                  )
                  .map((person) => (
                    <option key={person.user_id} value={person.user_id}>
                      {person.name} ·{" "}
                      {person.identity === "partner"
                        ? "合伙人"
                        : person.identity === "administration"
                          ? "行政财务"
                          : "律师"}
                    </option>
                  ))}
              </select>
              <Button icon={Plus} loading={loading} onClick={() => void add()}>
                添加成员
              </Button>
            </div>
            <div className="handover-form">
              <select
                value={leadId}
                onChange={(e) => setLeadId(e.target.value)}
              >
                <option value="">选择新的主要承办</option>
                {directoryList
                  .filter(
                    (person) =>
                      team.some(
                        (member) => member.user_id === person.user_id,
                      ) || person.user_id === matter.lead_id,
                  )
                  .map((person) => (
                    <option key={person.user_id} value={person.user_id}>
                      {person.name} ·{" "}
                      {person.identity === "partner" ? "合伙人" : "律师"}
                    </option>
                  ))}
              </select>
              <Button
                variant="secondary"
                loading={loading}
                disabled={!leadId || leadId === matter.lead_id}
                onClick={() => void handover()}
              >
                设为主要承办
              </Button>
            </div>
          </>
        )}
      </section>
      <aside className="detail-side">
        <div className="aside-card accent">
          <Users size={21} />
          <strong>团队与人事分开管理</strong>
          <p>从案件中移除成员不会删除其律所人员档案，也不会影响其他案件。</p>
        </div>
      </aside>
    </div>
  );
}

function FilesTab({ actor, matter }: { actor: Actor; matter: Matter }) {
  const [records, setRecords] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("案件材料");
  type UploadMeta = {
    batchId: string;
    batchCount: number;
    batchSlot: number;
    idempotencyKey: string;
  };
  type UploadItem = UploadMeta & {
    name: string;
    progress: number;
    status: "uploading" | "ready" | "failed";
    message?: string;
    file?: File;
    documentId?: string;
  };
  const [uploads, setUploads] = useState<Record<string, UploadItem>>({});
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRecords(await files.list(matter.id));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [matter.id]);
  useEffect(() => {
    void load();
  }, [load]);
  async function performUpload(
    file: File,
    documentId?: string,
    queueId: string = crypto.randomUUID(),
    meta: UploadMeta = {
      batchId: crypto.randomUUID(),
      batchCount: 1,
      batchSlot: 0,
      idempotencyKey: crypto.randomUUID(),
    },
  ) {
    if (file.size > 50_000_000) {
      setUploads((current) => ({
        ...current,
        [queueId]: {
          ...meta,
          name: file.name,
          progress: 0,
          status: "failed",
          message: "超过单文件 50 MB 限制",
          file,
          documentId,
        },
      }));
      return;
    }
    setUploads((current) => ({
      ...current,
      [queueId]: {
        ...meta,
        name: file.name,
        progress: 0,
        status: "uploading",
        file,
        documentId,
      },
    }));
    try {
      await files.upload(
        matter.id,
        file,
        category,
        (value) =>
          setUploads((current) => ({
            ...current,
            [queueId]: { ...current[queueId], progress: value },
          })),
        documentId,
        meta,
      );
      setUploads((current) => ({
        ...current,
        [queueId]: { ...current[queueId], progress: 1, status: "ready" },
      }));
      await load();
    } catch (e) {
      setUploads((current) => ({
        ...current,
        [queueId]: {
          ...current[queueId],
          status: "failed",
          message: errorMessage(e),
        },
      }));
    }
  }
  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(event.target.files ?? []);
    if (!selected.length) return;
    if (selected.length > 20) {
      setError("每批最多上传 20 个文件");
      event.target.value = "";
      return;
    }
    setError("");
    const batchId = crypto.randomUUID();
    const batchCount = selected.length;
    selected.forEach(
      (file, batchSlot) =>
        void performUpload(file, undefined, crypto.randomUUID(), {
          batchId,
          batchCount,
          batchSlot,
          idempotencyKey: crypto.randomUUID(),
        }),
    );
    event.target.value = "";
  }
  return (
    <div className="detail-grid">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">FILES & HISTORY</p>
            <h2>案件文件</h2>
            <p className="muted">
              每次更新都会保留历史版本；文件默认下载查看。
            </p>
          </div>
          <div className="upload-controls">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option>案件材料</option>
              <option>合同</option>
              <option>律师函</option>
              <option>公函</option>
              <option>利冲依据</option>
              <option>票据</option>
              <option>归档材料</option>
              <option>其他</option>
            </select>
            <label className="button button-primary">
              <Upload size={16} />
              上传文件
              <input
                type="file"
                multiple
                hidden
                onChange={(event) => void upload(event)}
              />
            </label>
          </div>
        </div>
        {error && <ErrorBox message={error} />}
        {Object.entries(uploads).map(([id, item]) => (
          <div className={`upload-row upload-${item.status}`} key={id}>
            <span className="upload-name">{item.name}</span>
            {item.status === "uploading" && (
              <>
                <div className="upload-progress-inline">
                  <i style={{ width: `${Math.round(item.progress * 100)}%` }} />
                </div>
                <small>{Math.round(item.progress * 100)}%</small>
              </>
            )}
            {item.status === "ready" && <Badge tone="success">上传成功</Badge>}
            {item.status === "failed" && (
              <>
                <Badge tone="danger">上传失败</Badge>
                <small>{item.message || "请重试"}</small>
                {item.file && (
                  <Button
                    variant="secondary"
                    className="small"
                    onClick={() =>
                      void performUpload(
                        item.file as File,
                        item.documentId,
                        id,
                        item,
                      )
                    }
                  >
                    重试
                  </Button>
                )}
              </>
            )}
          </div>
        ))}
        {loading ? (
          <LoadingRows count={4} />
        ) : records.length ? (
          <div className="file-list">
            {records.map((file) => {
              const ready = file.status === "ready" || !file.status;
              const current = file.current !== false;
              return (
                <div
                  className={`file-row ${current ? "" : "file-row-history"}`}
                  key={file.id}
                >
                  <span className="file-icon">
                    <FileText size={19} />
                  </span>
                  <div className="file-copy">
                    <strong>{file.name || file.file_name}</strong>
                    <span>
                      {file.category || "未分类"} ·{" "}
                      {file.size
                        ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
                        : "大小未知"}{" "}
                      · 第 {file.revision ?? 1} 版 · {fmtDate(file.created_at)}
                    </span>
                    {!current && <small>历史版本</small>}
                    {file.failure_message && (
                      <small className="danger-text">
                        {file.failure_message}
                      </small>
                    )}
                  </div>
                  <Badge
                    tone={
                      file.status === "failed"
                        ? "danger"
                        : ready
                          ? current
                            ? "success"
                            : "neutral"
                          : "warning"
                    }
                  >
                    {file.status === "failed"
                      ? "上传失败"
                      : ready
                        ? current
                          ? "当前文件"
                          : "历史版本"
                        : labelStatus(file.status)}
                  </Badge>
                  <div className="file-actions">
                    {ready && file.content_type === "application/pdf" && (
                      <a
                        className="icon-button"
                        title="在线预览 PDF"
                        aria-label="在线预览 PDF"
                        target="_blank"
                        rel="noreferrer"
                        href={files.contentUrl(file.id, true)}
                      >
                        <FileText size={17} />
                      </a>
                    )}
                    {ready && (
                      <a
                        className="icon-button"
                        title="下载文件"
                        aria-label="下载文件"
                        href={files.contentUrl(file.id)}
                      >
                        <Download size={17} />
                      </a>
                    )}
                    {current &&
                      ready &&
                      can(actor, "file.write") &&
                      file.document_id && (
                        <label className="button button-secondary small">
                          更新这份文件
                          <input
                            type="file"
                            hidden
                            onChange={(event) => {
                              const next = event.target.files?.[0];
                              if (next)
                                void performUpload(
                                  next,
                                  file.document_id,
                                  crypto.randomUUID(),
                                  {
                                    batchId: crypto.randomUUID(),
                                    batchCount: 1,
                                    batchSlot: 0,
                                    idempotencyKey: crypto.randomUUID(),
                                  },
                                );
                              event.target.value = "";
                            }}
                          />
                        </label>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty
            icon={FolderOpen}
            title="还没有文件"
            detail="上传合同、函件、扫描件或归档材料。"
          />
        )}
      </section>
      <aside className="detail-side">
        <div className="aside-card">
          <p className="eyebrow">UPLOAD RULES</p>
          <h3>上传说明</h3>
          <ul className="plain-list">
            <li>单文件不超过 50 MB</li>
            <li>每批最多 20 个文件</li>
            <li>失败文件可单独重试</li>
            <li>PDF 支持在线预览</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}

function StampTab({ actor, matter }: { actor: Actor; matter: Matter }) {
  const [records, setRecords] = useState<Record<string, unknown>[]>([]);
  const [matterFiles, setMatterFiles] = useState<FileRecord[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [fileId, setFileId] = useState("");
  const [fileType, setFileType] = useState("contract");
  const [reason, setReason] = useState("");
  const [resultFileId, setResultFileId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stampRecords, fileRecords] = await Promise.all([
        stamp.list(matter.id),
        files.list(matter.id),
      ]);
      setRecords(stampRecords);
      setMatterFiles(
        fileRecords.filter((file) => file.status === "ready" || !file.status),
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [matter.id]);
  useEffect(() => {
    void load();
  }, [load]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await stamp.create({
        matter_id: matter.id,
        file_id: fileId,
        file_type: fileType,
        note: reason,
      });
      setShowForm(false);
      setFileId("");
      setReason("");
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function decide(id: string, outcome: "approve" | "reject") {
    const decisionReason =
      window.prompt(
        outcome === "reject" ? "请填写拒绝理由" : "审批意见（可选）",
      ) ?? "";
    if (outcome === "reject" && !decisionReason.trim()) return;
    setSaving(true);
    setError("");
    try {
      await stamp.decision(matter.id, id, outcome, { reason: decisionReason });
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function handle(id: string) {
    if (!resultFileId) return setError("请选择办理结果文件");
    setSaving(true);
    setError("");
    try {
      await stamp.handle(matter.id, id, {
        result_file_id: resultFileId,
        result_note: "已补充办理结果文件",
      });
      setResultFileId("");
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="detail-grid">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">STAMP & LETTERS</p>
            <h2>用章与出函</h2>
            <p className="muted">
              律师函、公函等非实质性文件直接办理；合同等实质性文件需要合伙人审批。办理完成后可关联结果附件。
            </p>
          </div>
          {can(actor, "stamp.create") && (
            <Button icon={Plus} onClick={() => setShowForm((v) => !v)}>
              申请用章
            </Button>
          )}
        </div>
        {error && <ErrorBox message={error} />}
        {showForm && (
          <form className="inline-card" onSubmit={submit}>
            <div className="form-grid">
              <Field label="文件" required>
                <select
                  value={fileId}
                  onChange={(e) => setFileId(e.target.value)}
                  required
                >
                  <option value="">选择已上传文件</option>
                  {matterFiles.map((file) => (
                    <option key={file.id} value={file.id}>
                      {file.name || file.file_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="文件类型" required>
                <select
                  value={fileType}
                  onChange={(e) => setFileType(e.target.value)}
                >
                  <option value="contract">合同</option>
                  <option value="agreement">协议</option>
                  <option value="lawyer_letter">律师函</option>
                  <option value="official_letter">公函</option>
                  <option value="public_letter">函件</option>
                </select>
              </Field>
            </div>
            <Field label="申请说明">
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="补充说明用章用途"
              />
            </Field>
            <div className="form-actions">
              <Button variant="secondary" onClick={() => setShowForm(false)}>
                取消
              </Button>
              <Button type="submit" loading={saving}>
                提交申请
              </Button>
            </div>
          </form>
        )}
        {loading ? (
          <LoadingRows count={3} />
        ) : records.length ? (
          <div className="request-list">
            {records.map((record, index) => {
              const status = String(record.status ?? "");
              const pendingApproval = status === "pending_approval";
              const pendingHandling = status === "pending_handling";
              return (
                <div className="request-row" key={String(record.id ?? index)}>
                  <div className="request-icon">
                    <Stamp size={18} />
                  </div>
                  <div>
                    <strong>
                      {String(
                        record.file_name ??
                          record.display_name ??
                          record.file_type ??
                          "用章申请",
                      )}
                    </strong>
                    <span>{String(record.note ?? record.purpose ?? "—")}</span>
                    <small>
                      {fmtDateTime(String(record.created_at ?? ""))} ·{" "}
                      {labelStatus(status)}
                    </small>
                    {pendingHandling && can(actor, "stamp.handle") && (
                      <div className="request-inline-action">
                        <select
                          aria-label="选择盖章结果附件"
                          value={resultFileId}
                          onChange={(e) => setResultFileId(e.target.value)}
                        >
                          <option value="">选择盖章结果附件</option>
                          {matterFiles.map((file) => (
                            <option key={file.id} value={file.id}>
                              {file.name || file.file_name}
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="secondary"
                          className="small"
                          loading={saving}
                          onClick={() => void handle(String(record.id))}
                        >
                          办理完成
                        </Button>
                      </div>
                    )}
                    {pendingApproval &&
                      actor.identity === "partner" &&
                      can(actor, "stamp.approve") &&
                      record.applicant_id !== actor.id && (
                        <div className="request-inline-action">
                          <Button
                            variant="secondary"
                            className="small"
                            loading={saving}
                            onClick={() =>
                              void decide(String(record.id), "approve")
                            }
                          >
                            批准
                          </Button>
                          <Button
                            variant="ghost"
                            className="small"
                            onClick={() =>
                              void decide(String(record.id), "reject")
                            }
                          >
                            拒绝
                          </Button>
                        </div>
                      )}
                  </div>
                  <Badge
                    tone={
                      /approved|handled|completed/i.test(status)
                        ? "success"
                        : /reject|return/i.test(status)
                          ? "danger"
                          : "warning"
                    }
                  >
                    {labelStatus(status)}
                  </Badge>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty
            icon={Stamp}
            title="还没有用章申请"
            detail="选择具体文件后发起申请。"
          />
        )}
      </section>
      <aside className="detail-side">
        <div className="aside-card accent">
          <Stamp size={21} />
          <strong>审批绑定具体文件</strong>
          <p>
            替换待审文件后需要重新提交；办理完成后可补传盖章扫描件作为结果附件。
          </p>
        </div>
      </aside>
    </div>
  );
}

function FinanceTab({ actor, matter }: { actor: Actor; matter: Matter }) {
  const [fees, setFees] = useState<Record<string, unknown>[]>([]);
  const [receipts, setReceipts] = useState<Record<string, unknown>[]>([]);
  const [refunds, setRefunds] = useState<Record<string, unknown>[]>([]);
  const [invoices, setInvoices] = useState<Record<string, unknown>[]>([]);
  const [expenses, setExpenses] = useState<Record<string, unknown>[]>([]);
  const [matterFiles, setMatterFiles] = useState<FileRecord[]>([]);
  const [mode, setMode] = useState<
    "expense" | "fee" | "receipt" | "refund" | "invoice"
  >("expense");
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [feeId, setFeeId] = useState("");
  const [amount, setAmount] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerTaxNo, setBuyerTaxNo] = useState("");
  const [reimbursementNote, setReimbursementNote] = useState("");
  const [reimbursedOn, setReimbursedOn] = useState("");
  const [fileId, setFileId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [money, expenseList, fileList] = await Promise.all([
        finance.fees(matter.id),
        finance.expenses(matter.id),
        files.list(matter.id),
      ]);
      setFees(money.fees);
      setReceipts(money.receipts);
      setRefunds(money.refunds);
      setInvoices(money.invoices);
      setExpenses(expenseList);
      setMatterFiles(
        fileList.filter((file) => file.status === "ready" || !file.status),
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [matter.id]);
  useEffect(() => {
    void load();
  }, [load]);
  function resetForm() {
    setAmount("");
    setTitle("");
    setNote("");
    setFeeId("");
    setPaymentMethod("");
    setBuyerName("");
    setBuyerTaxNo("");
    setReimbursementNote("");
    setReimbursedOn("");
    setFileId("");
    setDate(new Date().toISOString().slice(0, 10));
    setEditingExpense(null);
  }
  function openCreate(next: typeof mode) {
    resetForm();
    setMode(next);
    setShowForm(true);
  }
  function editExpense(record: Record<string, unknown>) {
    setMode("expense");
    setEditingExpense(record);
    setAmount(String(Number(record.amount_minor ?? 0) / 100));
    setNote(String(record.description ?? ""));
    setDate(
      String(record.expense_date ?? new Date().toISOString().slice(0, 10)),
    );
    setReimbursementNote(String(record.reimbursement_note ?? ""));
    setReimbursedOn(String(record.reimbursed_on ?? ""));
    setFileId(String(record.file_id ?? ""));
    setShowForm(true);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const minor = Math.round(Number(amount) * 100);
      if (mode === "expense") {
        if (editingExpense?.id)
          await finance.updateExpense(matter.id, String(editingExpense.id), {
            amount_minor: minor,
            description: note,
            expense_date: date,
            reimbursement_note: reimbursementNote,
            reimbursed_on: reimbursedOn || null,
            file_id: fileId || null,
          });
        else
          await finance.addExpense(matter.id, {
            amount: minor,
            description: note,
            expense_date: date,
            reimbursement_note: reimbursementNote,
            reimbursed_on: reimbursedOn || null,
            file_id: fileId || null,
          });
      } else if (mode === "fee")
        await finance.addFee(matter.id, {
          amount: minor,
          title,
          note,
          currency: matter.currency || "CNY",
        });
      else if (mode === "receipt") {
        if (!feeId) throw new Error("请选择对应的收费项目");
        await finance.addReceipt(matter.id, feeId, {
          amount: minor,
          date,
          payment_method: paymentMethod,
          note,
          file_id: fileId || null,
        });
      } else if (mode === "refund") {
        if (!feeId) throw new Error("请选择对应的收费项目");
        await finance.addRefund(matter.id, feeId, {
          amount: minor,
          date,
          note,
          file_id: fileId || null,
        });
      } else
        await finance.requestInvoice(matter.id, {
          fee_id: feeId || null,
          amount_minor: minor,
          currency: matter.currency || "CNY",
          buyer_name: buyerName,
          buyer_tax_no: buyerTaxNo,
          request_note: note,
        });
      resetForm();
      setShowForm(false);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function voidRecord(
    kind: "fee" | "receipt" | "refund" | "expense" | "invoice",
    record: Record<string, unknown>,
  ) {
    const reason = window.prompt("请填写作废原因")?.trim();
    if (!reason) return;
    setSaving(true);
    setError("");
    try {
      if (kind === "fee")
        await finance.voidFee(matter.id, String(record.id), reason);
      else if (kind === "receipt")
        await finance.voidReceipt(matter.id, String(record.id), reason);
      else if (kind === "refund")
        await finance.voidRefund(matter.id, String(record.id), reason);
      else if (kind === "expense")
        await finance.voidExpense(matter.id, String(record.id), reason);
      else await finance.voidInvoice(matter.id, String(record.id), reason);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function confirmInvoice(record: Record<string, unknown>) {
    const issued = window.prompt("请输入发票号码；留空将退回申请")?.trim();
    setSaving(true);
    setError("");
    try {
      if (issued)
        await finance.confirmInvoice(matter.id, String(record.id), {
          status: "issued",
          invoice_no: issued,
          issued_on: new Date().toISOString().slice(0, 10),
          result_note: "已开具",
        });
      else
        await finance.confirmInvoice(matter.id, String(record.id), {
          status: "rejected",
          result_note: "资料不完整，请补充后重新申请",
        });
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  const canExpense =
    can(actor, "expense.write", "participating") ||
    can(actor, "expense.manage");
  const canFee = can(actor, "fee.write") || can(actor, "fee.manage");
  const canReceipt = can(actor, "invoice.confirm");
  const canInvoice = can(actor, "invoice.request");
  const tabs: Array<[typeof mode, string, number]> = [
    ["expense", "案件经费", expenses.length],
    ["fee", "收费约定", fees.length],
    ["receipt", "实收", receipts.length],
    ["refund", "退款", refunds.length],
    ["invoice", "开票", invoices.length],
  ];
  const records =
    mode === "expense"
      ? expenses
      : mode === "fee"
        ? fees
        : mode === "receipt"
          ? receipts
          : mode === "refund"
            ? refunds
            : invoices;
  return (
    <div className="detail-grid">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">FEES & EXPENSES</p>
            <h2>收费与经费</h2>
            <p className="muted">
              收费、实收、退款、开票与案件经费分别记录；首期不替代律所财务总账。
            </p>
          </div>
          <div className="inline-actions">
            {((mode === "expense" && canExpense) ||
              (mode === "fee" && canFee) ||
              ((mode === "receipt" || mode === "refund") && canReceipt) ||
              (mode === "invoice" && canInvoice)) && (
              <Button
                variant="secondary"
                icon={Plus}
                onClick={() => openCreate(mode)}
              >
                {mode === "expense"
                  ? "新增经费"
                  : mode === "fee"
                    ? "新增收费约定"
                    : mode === "receipt"
                      ? "登记实收"
                      : mode === "refund"
                        ? "登记退款"
                        : "申请开票"}
              </Button>
            )}
          </div>
        </div>
        <div className="segmented content-switch">
          {tabs.map(([id, label, count]) => (
            <button
              key={id}
              className={mode === id ? "selected" : ""}
              onClick={() => setMode(id)}
            >
              {label}（{count}）
            </button>
          ))}
        </div>
        {error && <ErrorBox message={error} />}
        {showForm && (
          <form className="inline-card" onSubmit={submit}>
            <div className="form-grid">
              {mode === "expense" && (
                <>
                  <Field label="金额（元）" required>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="费用日期" required>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="费用说明" required>
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      required
                      placeholder="例如：差旅交通费"
                    />
                  </Field>
                  <Field label="报销备注">
                    <input
                      value={reimbursementNote}
                      onChange={(e) => setReimbursementNote(e.target.value)}
                      placeholder="可选，记录报销情况"
                    />
                  </Field>
                  <Field label="已报销日期">
                    <input
                      type="date"
                      value={reimbursedOn}
                      onChange={(e) => setReimbursedOn(e.target.value)}
                    />
                  </Field>
                </>
              )}
              {(mode === "fee" ||
                mode === "receipt" ||
                mode === "refund" ||
                mode === "invoice") && (
                <Field label="金额（元）" required>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </Field>
              )}
              {mode === "fee" && (
                <Field label="收费项目" required>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    placeholder="例如：首期律师费"
                  />
                </Field>
              )}
              {(mode === "receipt" || mode === "refund") && (
                <Field label="对应收费项目" required>
                  <select
                    value={feeId}
                    onChange={(e) => setFeeId(e.target.value)}
                    required
                  >
                    <option value="">选择收费项目</option>
                    {fees
                      .filter((fee) => fee.status !== "void")
                      .map((fee) => (
                        <option key={String(fee.id)} value={String(fee.id)}>
                          {String(fee.title ?? "收费项目")} ·{" "}
                          {fmtMoney(
                            Number(fee.amount_minor ?? 0),
                            String(fee.currency ?? "CNY"),
                          )}
                        </option>
                      ))}
                  </select>
                </Field>
              )}
              {mode === "invoice" && (
                <>
                  <Field label="对应收费项目">
                    <select
                      value={feeId}
                      onChange={(e) => setFeeId(e.target.value)}
                    >
                      <option value="">不绑定具体收费项目</option>
                      {fees
                        .filter((fee) => fee.status !== "void")
                        .map((fee) => (
                          <option key={String(fee.id)} value={String(fee.id)}>
                            {String(fee.title ?? "收费项目")}
                          </option>
                        ))}
                    </select>
                  </Field>
                  <Field label="购方名称" required>
                    <input
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="购方税号">
                    <input
                      value={buyerTaxNo}
                      onChange={(e) => setBuyerTaxNo(e.target.value)}
                    />
                  </Field>
                </>
              )}
              {(mode === "receipt" || mode === "refund") && (
                <Field label="日期" required>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </Field>
              )}
              {mode === "receipt" && (
                <Field label="收款方式">
                  <input
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    placeholder="银行转账、现金等"
                  />
                </Field>
              )}
              <Field
                label={
                  mode === "refund"
                    ? "退款原因"
                    : mode === "invoice"
                      ? "申请说明"
                      : "备注"
                }
              >
                <input value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              {mode !== "fee" && mode !== "invoice" && (
                <Field label="凭证文件">
                  <select
                    value={fileId}
                    onChange={(e) => setFileId(e.target.value)}
                  >
                    <option value="">不关联文件</option>
                    {matterFiles.map((file) => (
                      <option key={file.id} value={file.id}>
                        {file.name || file.file_name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
            <div className="form-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
              >
                取消
              </Button>
              <Button type="submit" loading={saving}>
                保存记录
              </Button>
            </div>
          </form>
        )}
        {loading ? (
          <LoadingRows count={3} />
        ) : records.length ? (
          <div className="finance-list">
            {records.map((record, index) => {
              const status = String(record.status ?? "active");
              const label =
                mode === "expense"
                  ? String(record.description ?? "经费记录")
                  : mode === "fee"
                    ? String(record.title ?? "收费约定")
                    : mode === "receipt"
                      ? "实收 · " + fmtDate(String(record.received_on ?? ""))
                      : mode === "refund"
                        ? "退款 · " + fmtDate(String(record.refunded_on ?? ""))
                        : String(record.buyer_name ?? "开票申请");
              const recordAmount = Number(record.amount_minor ?? 0);
              return (
                <div className="finance-row" key={String(record.id ?? index)}>
                  <div>
                    <strong>{label}</strong>
                    <span>
                      {fmtMoney(
                        recordAmount,
                        String(record.currency ?? matter.currency ?? "CNY"),
                      )}{" "}
                      · {fmtDateTime(String(record.created_at ?? ""))}
                      {status === "void" ? " · 已作废" : ""}
                    </span>
                    {mode === "expense" &&
                      Boolean(
                        record.reimbursement_note || record.reimbursed_on,
                      ) && (
                        <small>
                          报销：{String(record.reimbursement_note ?? "")}
                          {record.reimbursed_on
                            ? " · " + String(record.reimbursed_on)
                            : ""}
                        </small>
                      )}
                    {mode === "invoice" && (
                      <small>
                        {labelStatus(status)}
                        {record.invoice_no
                          ? " · 发票号 " + String(record.invoice_no)
                          : ""}
                      </small>
                    )}
                  </div>
                  <div className="finance-row-actions">
                    {mode === "expense" && status !== "void" && canExpense && (
                      <>
                        <Button
                          variant="ghost"
                          className="small"
                          onClick={() => editExpense(record)}
                        >
                          更正
                        </Button>
                        <Button
                          variant="ghost"
                          className="small"
                          onClick={() => void voidRecord("expense", record)}
                        >
                          作废
                        </Button>
                      </>
                    )}
                    {mode === "fee" && status !== "void" && canReceipt && (
                      <Button
                        variant="ghost"
                        className="small"
                        onClick={() => void voidRecord("fee", record)}
                      >
                        作废
                      </Button>
                    )}
                    {(mode === "receipt" || mode === "refund") &&
                      status !== "void" &&
                      canReceipt && (
                        <Button
                          variant="ghost"
                          className="small"
                          onClick={() => void voidRecord(mode, record)}
                        >
                          作废
                        </Button>
                      )}
                    {mode === "invoice" &&
                      status === "requested" &&
                      can(actor, "invoice.confirm") && (
                        <Button
                          variant="secondary"
                          className="small"
                          loading={saving}
                          onClick={() => void confirmInvoice(record)}
                        >
                          处理
                        </Button>
                      )}
                    {mode === "invoice" &&
                      status !== "void" &&
                      can(actor, "invoice.confirm") &&
                      status !== "requested" && (
                        <Button
                          variant="ghost"
                          className="small"
                          onClick={() => void voidRecord("invoice", record)}
                        >
                          作废
                        </Button>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty
            icon={BriefcaseBusiness}
            title={
              mode === "expense"
                ? "还没有经费记录"
                : mode === "fee"
                  ? "还没有收费约定"
                  : mode === "receipt"
                    ? "还没有实收记录"
                    : mode === "refund"
                      ? "还没有退款记录"
                      : "还没有开票申请"
            }
            detail="在案件中登记即可形成可回看的业务台账。"
          />
        )}
      </section>
      <aside className="detail-side">
        <div className="aside-card">
          <p className="eyebrow">SCOPE</p>
          <h3>案件财务台账</h3>
          <p>
            参与律师直接登记经费；行政财务可确认实收、退款和开票结果。作废保留原记录和原因。
          </p>
        </div>
      </aside>
    </div>
  );
}

function ArchiveTab({ actor, matter }: { actor: Actor; matter: Matter }) {
  const [record, setRecord] = useState<Record<string, unknown> | null>(null);
  const [availableFiles, setAvailableFiles] = useState<FileRecord[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [request, fileList] = await Promise.all([
        archive.get(matter.id),
        files.list(matter.id),
      ]);
      setRecord(
        request?.archive_request && typeof request.archive_request === "object"
          ? (request.archive_request as Record<string, unknown>)
          : null,
      );
      setAvailableFiles(
        fileList.filter((file) => file.status === "ready" || !file.status),
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [matter.id]);
  useEffect(() => {
    void load();
  }, [load]);
  async function submit() {
    if (!selectedFiles.length) return setError("请至少选择一个归档材料");
    setSaving(true);
    setError("");
    try {
      await archive.submit({
        matter_id: matter.id,
        note,
        files: selectedFiles.map((file_id) => ({
          file_id,
          category: "archive_material",
        })),
      });
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function confirm() {
    if (!record?.id) return;
    setSaving(true);
    setError("");
    try {
      await archive.confirm(matter.id, String(record.id));
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function returnToSupplement() {
    if (!record?.id) return;
    const reason = window.prompt("请填写退回原因")?.trim();
    if (!reason) return;
    setSaving(true);
    setError("");
    try {
      await archive.return(matter.id, String(record.id), { reason });
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="detail-grid">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">ARCHIVE</p>
            <h2>结案归档</h2>
            <p className="muted">
              律师提交材料，行政财务核对并确认；欠费不阻止归档。
            </p>
          </div>
          <Badge
            tone={
              /archived/i.test(String(record?.status)) ? "success" : "warning"
            }
          >
            {labelStatus(record?.status || "pending")}
          </Badge>
        </div>
        {error && <ErrorBox message={error} />}
        {loading ? (
          <LoadingRows count={3} />
        ) : record ? (
          <div className="archive-status">
            <div className="archive-step done">
              <span>
                <Check size={17} />
              </span>
              <div>
                <strong>已提交归档</strong>
                <small>{fmtDateTime(String(record.created_at ?? ""))}</small>
              </div>
            </div>
            <div
              className={`archive-step ${/archived/i.test(String(record.status)) ? "done" : "current"}`}
            >
              <span>
                {/archived/i.test(String(record.status)) ? (
                  <Check size={17} />
                ) : (
                  <Clock3 size={17} />
                )}
              </span>
              <div>
                <strong>
                  {/archived/i.test(String(record.status))
                    ? "行政财务已确认"
                    : "待行政财务核对"}
                </strong>
                <small>{String(record.note ?? "请核对归档材料")}</small>
              </div>
            </div>
          </div>
        ) : (
          <div className="archive-submit">
            <div className="archive-illustration">
              <FileArchive size={28} />
            </div>
            <h3>准备好归档了吗？</h3>
            <p>请先将应归档的案件材料上传到“文件与历史”，再提交归档核对。</p>
            {availableFiles.length ? (
              <div className="archive-files">
                {availableFiles.slice(0, 20).map((file) => (
                  <label key={file.id}>
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={(event) =>
                        setSelectedFiles((current) =>
                          event.target.checked
                            ? [...current, file.id]
                            : current.filter((id) => id !== file.id),
                        )
                      }
                    />
                    <span>{file.name || file.file_name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="muted">当前案件还没有可归档的文件。</p>
            )}
            <Field label="补充说明">
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="可选，说明本次归档范围"
              />
            </Field>
            <Button
              icon={Archive}
              loading={saving}
              onClick={() => void submit()}
              disabled={!can(actor, "archive.submit")}
            >
              提交归档
            </Button>
          </div>
        )}
        {record &&
          !/archived/i.test(String(record.status)) &&
          can(actor, "archive.confirm") && (
            <div className="form-actions">
              <Button
                icon={Check}
                loading={saving}
                onClick={() => void confirm()}
              >
                确认归档
              </Button>
              <Button
                variant="secondary"
                loading={saving}
                onClick={() => void returnToSupplement()}
              >
                退回补充
              </Button>
            </div>
          )}
      </section>
      <aside className="detail-side">
        <div className="aside-card accent">
          <Archive size={21} />
          <strong>归档是一条独立流程</strong>
          <p>
            归档提交和财务确认不会要求补齐付款，也不会改变已经留存的审批与文件历史。
          </p>
        </div>
      </aside>
    </div>
  );
}

function TemplatesPage({ actor }: { actor: Actor }) {
  const queryMatter =
    new URLSearchParams(window.location.hash.split("?")[1] ?? "").get(
      "matter",
    ) ?? "";
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [authorizedMatters, setAuthorizedMatters] = useState<Matter[]>([]);
  const [matterSearch, setMatterSearch] = useState("");
  const [matterLoading, setMatterLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [matterId, setMatterId] = useState(queryMatter);
  const [templateValues, setTemplateValues] = useState<Record<string, string>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<ReactNode>("");
  const [generatedDownload, setGeneratedDownload] = useState<{
    url: string;
    name: string;
  }>();
  const autoFields = new Set([
    "title",
    "number",
    "business_type",
    "scope",
    "fee_note",
    "start_date",
    "end_date",
    "client_names",
    "opponent_names",
    "lead_name",
    "firm_name",
    "generated_date",
    "fee_amount_yuan",
    "parties",
  ]);
  const selectedTemplate = items.find(
    (item, index) => String(item.id ?? index) === selected,
  );
  const customFields = (
    Array.isArray(selectedTemplate?.fields) ? selectedTemplate.fields : []
  )
    .map((field) => field as Record<string, unknown>)
    .filter(
      (field) =>
        typeof field.key === "string" && !autoFields.has(String(field.key)),
    );
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const [templateResult, matterResult] = await Promise.allSettled([
      templates.list(),
      matters.list({ limit: 100 }),
    ]);
    if (templateResult.status === "fulfilled") setItems(templateResult.value);
    else setError(errorMessage(templateResult.reason));
    if (matterResult.status === "fulfilled")
      setAuthorizedMatters(matterResult.value);
    else if (templateResult.status === "fulfilled")
      setError(errorMessage(matterResult.reason));
    setLoading(false);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (
      queryMatter &&
      authorizedMatters.some((matter) => matter.id === queryMatter)
    )
      setMatterId(queryMatter);
  }, [queryMatter, authorizedMatters]);
  useEffect(
    () => () => {
      if (generatedDownload?.url) URL.revokeObjectURL(generatedDownload.url);
    },
    [generatedDownload],
  );
  function chooseTemplate(id: string) {
    setSelected(id);
    setTemplateValues({});
  }
  async function searchMatters() {
    setMatterLoading(true);
    setError("");
    try {
      setAuthorizedMatters(
        await matters.list({ q: matterSearch.trim(), limit: 100 }),
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setMatterLoading(false);
    }
  }
  async function uploadTemplate(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      setError("模板必须是 DOCX 文件");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const template = await templates.upload(file);
      setItems((current) => [template, ...current]);
      setNotice("模板已上传并可用于生成文书");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }
  async function generate() {
    if (!selected || !matterId) return setError("请选择模板和有权访问的案件");
    setGenerating(true);
    setError("");
    try {
      const result = await templates.generate({
        template_id: selected,
        matter_id: matterId,
        values: templateValues,
      });
      if (result.download_url) {
        const name = result.file_name || "标准文书-生成.docx";
        setGeneratedDownload({ url: result.download_url, name });
        setNotice(
          <span>
            文书已生成，可以下载编辑。
            <a
              className="generated-download"
              href={result.download_url}
              download={name}
            >
              再次下载 {name}
            </a>
          </span>,
        );
        const link = document.createElement("a");
        link.href = result.download_url;
        link.download = name;
        link.click();
      } else setNotice(`文书已生成（文件 ID：${result.file_id}）`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setGenerating(false);
    }
  }
  return (
    <div className="page">
      <SectionTitle
        eyebrow="DOCUMENT TEMPLATES"
        title="标准文书"
        description="选择有权访问的案件和律所模板。案件资料会自动带入，只需补充模板要求的自定义字段。生成文件默认下载查看。"
      />
      {error && <ErrorBox message={error} />}
      {notice && <SuccessBox>{notice}</SuccessBox>}
      <div className="template-layout">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">AVAILABLE TEMPLATES</p>
              <h2>模板库</h2>
            </div>
            {can(actor, "template.manage") && (
              <label className="button button-secondary">
                <Upload size={16} />
                {uploading ? "上传中…" : "上传模板"}
                <input
                  type="file"
                  accept=".docx"
                  hidden
                  onChange={(event) => void uploadTemplate(event)}
                />
              </label>
            )}
          </div>
          {loading ? (
            <LoadingRows count={4} />
          ) : items.length ? (
            <div className="template-grid">
              {items.map((item, index) => {
                const id = String(item.id ?? index);
                return (
                  <button
                    className={`template-card ${selected === id ? "selected" : ""}`}
                    key={id}
                    onClick={() => chooseTemplate(id)}
                  >
                    <span className="template-icon">
                      <FileText size={22} />
                    </span>
                    <strong>
                      {String(item.name ?? item.title ?? "未命名模板")}
                    </strong>
                    <span>
                      {String(item.category ?? "Word 模板")} ·{" "}
                      {String(
                        item.updated_at
                          ? fmtDate(String(item.updated_at))
                          : "律所模板",
                      )}
                    </span>
                    {selected === id && (
                      <span className="template-check">
                        <Check size={15} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <Empty
              icon={FilePenLine}
              title="还没有文书模板"
              detail="请由模板管理员上传首批律所模板。"
            />
          )}
        </section>
        <aside className="detail-side">
          <div className="aside-card">
            <p className="eyebrow">GENERATE</p>
            <h3>生成可编辑文书</h3>
            <div className="matter-picker">
              <Field label="查找案件">
                <input
                  value={matterSearch}
                  onChange={(e) => setMatterSearch(e.target.value)}
                  placeholder="按案件名称或编号搜索"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void searchMatters();
                    }
                  }}
                />
              </Field>
              <Button
                variant="secondary"
                loading={matterLoading}
                onClick={() => void searchMatters()}
              >
                搜索案件
              </Button>
            </div>
            <Field label="案件" required>
              <select
                value={matterId}
                onChange={(e) => setMatterId(e.target.value)}
                required
              >
                <option value="">选择有权访问的案件</option>
                {authorizedMatters.map((matter) => (
                  <option key={matter.id} value={matter.id}>
                    {matter.number ? `${matter.number} · ` : ""}
                    {matter.title}
                  </option>
                ))}
              </select>
            </Field>
            {authorizedMatters.length === 0 && (
              <p className="muted small-note">
                暂无可生成文书的案件。请先建立或加入案件。
              </p>
            )}
            {selected && customFields.length > 0 && (
              <div className="template-fields">
                <p className="eyebrow">CUSTOM FIELDS</p>
                <p className="muted small-note">
                  以下字段需要手动补充；案件名称、编号、当事人等标准字段会自动填充。
                </p>
                {customFields.map((field) => {
                  const key = String(field.key);
                  const label = String(field.label ?? key);
                  const type = String(field.type ?? "text");
                  return (
                    <Field
                      key={key}
                      label={label}
                      required={field.required !== false}
                    >
                      <input
                        type={
                          type === "date"
                            ? "date"
                            : type === "number"
                              ? "number"
                              : "text"
                        }
                        value={templateValues[key] ?? ""}
                        onChange={(e) =>
                          setTemplateValues((current) => ({
                            ...current,
                            [key]: e.target.value,
                          }))
                        }
                        required={field.required !== false}
                      />
                    </Field>
                  );
                })}
              </div>
            )}
            {selected && customFields.length === 0 && (
              <p className="muted small-note">
                此模板只使用案件自动字段，无需额外填写。
              </p>
            )}
            <Button
              className="button-wide"
              icon={Download}
              loading={generating}
              disabled={!selected || !matterId}
              onClick={() => void generate()}
            >
              生成并下载 Word
            </Button>
            <p className="muted small-note">
              生成件不会自动成为审批材料；需要用章或归档时，请在具体案件中上传。
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function StaffPage({ actor }: { actor: Actor }) {
  const [people, setPeople] = useState<Staff[]>([]);
  const [roleCatalog, setRoleCatalog] = useState<RoleCatalog>({
    roles: [],
    actions: [],
    scopes: {},
  });
  const [tab, setTab] = useState<"staff" | "roles">("staff");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createdPassword, setCreatedPassword] = useState("");
  const [secretLabel, setSecretLabel] = useState("账号已创建");
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [identity, setIdentity] = useState("lawyer");
  const [password, setPassword] = useState(
    () => `Qsy${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}!`,
  );
  const [roleIds, setRoleIds] = useState<string[]>(["lawyer"]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editDraft, setEditDraft] = useState<Partial<Staff>>({});
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const [personResult, roleResult] = await Promise.allSettled([
      staff.list(),
      roles.list(),
    ]);
    if (personResult.status === "fulfilled") setPeople(personResult.value);
    else setError(errorMessage(personResult.reason));
    if (roleResult.status === "fulfilled") setRoleCatalog(roleResult.value);
    setLoading(false);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  async function createPerson(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await staff.create({
        name,
        username,
        identity,
        password,
        role_ids: roleIds.length
          ? roleIds
          : [
              identity === "partner"
                ? "partner"
                : identity === "administration"
                  ? "administration"
                  : "lawyer",
            ],
      });
      setCreatedPassword(password);
      setSecretLabel("账号已创建");
      setName("");
      setUsername("");
      setPassword(
        `Qsy${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}!`,
      );
      setShowForm(false);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function resetPersonPassword(person: Staff) {
    const nextPassword =
      "Qsy" + crypto.randomUUID().replaceAll("-", "").slice(0, 12) + "!";
    setSaving(true);
    setError("");
    try {
      await staff.resetPassword(person.user_id, nextPassword);
      setCreatedPassword(nextPassword);
      setSecretLabel(person.name + "的临时密码已重置");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  function beginEdit(person: Staff) {
    const canStaffWrite = can(actor, "staff.write");
    const canRoleManage = can(actor, "role.manage");
    setEditingId(person.user_id);
    setEditDraft(
      canStaffWrite
        ? {
            name: person.name,
            identity: person.identity,
            employment_status: person.employment_status || "active",
            phone: person.phone || "",
            license_number: person.license_number || "",
            joined_on: person.joined_on || "",
            left_on: person.left_on || "",
            ...(canRoleManage ? { role_ids: person.role_ids || [] } : {}),
          }
        : canRoleManage
          ? { role_ids: person.role_ids || [] }
          : {},
    );
  }
  async function saveEdit() {
    if (!editingId) return;
    setSaving(true);
    setError("");
    try {
      await staff.update(editingId, editDraft);
      setEditingId("");
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function toggleAccount(person: Staff) {
    const active = !(
      person.account_active === false || person.account_active === 0
    );
    setSaving(true);
    setError("");
    try {
      await staff.status(person.user_id, !active);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  const identityLabel = (value?: string) =>
    value === "partner"
      ? "合伙人"
      : value === "administration"
        ? "行政财务"
        : value === "system"
          ? "系统管理员"
          : "律师";
  return (
    <div className="page">
      <SectionTitle
        eyebrow="PEOPLE & ACCESS"
        title="人员与权限"
        description="律所人员档案、登录账号和权限组分开维护。案件团队在案件中管理。"
        actions={
          tab === "staff" &&
          can(actor, "account.manage") &&
          can(actor, "role.manage") && (
            <Button icon={Plus} onClick={() => setShowForm((v) => !v)}>
              创建账号
            </Button>
          )
        }
      />
      <div className="segmented page-tabs">
        <button
          className={tab === "staff" ? "selected" : ""}
          onClick={() => setTab("staff")}
        >
          <Users size={16} />
          人员与账号
        </button>
        <button
          className={tab === "roles" ? "selected" : ""}
          onClick={() => setTab("roles")}
        >
          <ShieldCheck size={16} />
          权限组
        </button>
      </div>
      {error && <ErrorBox message={error} />}
      {createdPassword && (
        <SuccessBox>
          {secretLabel}。请安全交付这一次性显示的临时密码：
          <strong className="one-time-secret">{createdPassword}</strong>
          <button
            className="secret-dismiss"
            onClick={() => setCreatedPassword("")}
          >
            我已记录
          </button>
        </SuccessBox>
      )}
      {tab === "staff" ? (
        <section className="panel">
          {showForm && (
            <form className="inline-card" onSubmit={createPerson}>
              <div className="form-grid">
                <Field label="姓名" required>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Field>
                <Field label="账号" required>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </Field>
                <Field label="人事身份" required>
                  <select
                    value={identity}
                    onChange={(e) => {
                      const value = e.target.value;
                      setIdentity(value);
                      setRoleIds([
                        value === "partner"
                          ? "partner"
                          : value === "administration"
                            ? "administration"
                            : "lawyer",
                      ]);
                    }}
                  >
                    <option value="lawyer">律师</option>
                    <option value="partner">合伙人</option>
                    <option value="administration">行政财务</option>
                  </select>
                </Field>
                <Field
                  label="临时密码"
                  hint="创建后请通过安全方式交付，首次登录必须修改"
                  required
                >
                  <input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={12}
                    required
                  />
                </Field>
                <Field label="权限组" required>
                  <select
                    value={roleIds[0] ?? ""}
                    onChange={(e) => setRoleIds([e.target.value])}
                    required
                  >
                    <option value="">选择权限组</option>
                    {roleCatalog.roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="form-actions">
                <Button variant="secondary" onClick={() => setShowForm(false)}>
                  取消
                </Button>
                <Button type="submit" loading={saving}>
                  创建账号
                </Button>
              </div>
            </form>
          )}
          {loading ? (
            <LoadingRows count={5} />
          ) : people.length ? (
            <div className="staff-list">
              {people.map((person) => {
                const accountActive =
                  person.account_active !== false &&
                  person.account_active !== 0;
                const editing = editingId === person.user_id;
                return (
                  <div
                    className={`staff-card ${editing ? "staff-card-editing" : ""}`}
                    key={person.user_id}
                  >
                    <div className="staff-row">
                      <span className="avatar">
                        {person.name?.slice(0, 1) || "人"}
                      </span>
                      <div className="staff-main">
                        <strong>{person.name}</strong>
                        <span>
                          {person.username || "—"} ·{" "}
                          {identityLabel(person.identity)}
                        </span>
                      </div>
                      <div className="staff-statuses">
                        <Badge
                          tone={
                            person.employment_status === "left"
                              ? "danger"
                              : "success"
                          }
                        >
                          {person.employment_status === "left"
                            ? "已离职"
                            : "在职"}
                        </Badge>
                        <Badge tone={accountActive ? "success" : "neutral"}>
                          {accountActive ? "账号启用" : "账号停用"}
                        </Badge>
                      </div>
                      <span className="staff-detail">
                        {person.license_number ||
                          person.phone ||
                          "未填写执业信息"}
                      </span>
                      <div className="staff-actions">
                        {(can(actor, "staff.write") ||
                          can(actor, "role.manage")) && (
                          <Button
                            variant="ghost"
                            className="small"
                            onClick={() => beginEdit(person)}
                          >
                            编辑档案
                          </Button>
                        )}
                        {can(actor, "account.manage") && (
                          <>
                            <Button
                              variant="ghost"
                              className="small"
                              loading={saving}
                              onClick={() => void resetPersonPassword(person)}
                            >
                              重置密码
                            </Button>
                            <Button
                              variant="secondary"
                              className="small"
                              disabled={
                                saving ||
                                (person.user_id === actor.id && accountActive)
                              }
                              onClick={() => void toggleAccount(person)}
                            >
                              {accountActive ? "停用账号" : "启用账号"}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {editing && (
                      <div className="staff-editor">
                        {can(actor, "staff.write") && (
                          <div className="form-grid">
                            <Field label="姓名">
                              <input
                                value={String(editDraft.name ?? "")}
                                onChange={(e) =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    name: e.target.value,
                                  }))
                                }
                              />
                            </Field>
                            <Field label="人事身份">
                              <select
                                value={String(editDraft.identity ?? "lawyer")}
                                onChange={(e) =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    identity: e.target.value,
                                  }))
                                }
                              >
                                <option value="lawyer">律师</option>
                                <option value="partner">合伙人</option>
                                <option value="administration">行政财务</option>
                              </select>
                            </Field>
                            <Field label="执业证号">
                              <input
                                value={String(editDraft.license_number ?? "")}
                                onChange={(e) =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    license_number: e.target.value,
                                  }))
                                }
                              />
                            </Field>
                            <Field label="联系电话">
                              <input
                                value={String(editDraft.phone ?? "")}
                                onChange={(e) =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    phone: e.target.value,
                                  }))
                                }
                              />
                            </Field>
                            <Field label="入职日期">
                              <input
                                type="date"
                                value={String(editDraft.joined_on ?? "")}
                                onChange={(e) =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    joined_on: e.target.value,
                                  }))
                                }
                              />
                            </Field>
                            <Field label="人事状态">
                              <select
                                value={String(
                                  editDraft.employment_status ?? "active",
                                )}
                                onChange={(e) =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    employment_status: e.target.value,
                                  }))
                                }
                              >
                                <option value="active">在职</option>
                                <option value="left">离职</option>
                              </select>
                            </Field>
                            <Field label="离职日期">
                              <input
                                type="date"
                                value={String(editDraft.left_on ?? "")}
                                onChange={(e) =>
                                  setEditDraft((current) => ({
                                    ...current,
                                    left_on: e.target.value,
                                  }))
                                }
                              />
                            </Field>
                          </div>
                        )}
                        {can(actor, "role.manage") && (
                          <Field label="权限组">
                            <select
                              multiple
                              size={Math.min(4, roleCatalog.roles.length)}
                              value={
                                (editDraft.role_ids as string[] | undefined) ??
                                []
                              }
                              onChange={(e) =>
                                setEditDraft((current) => ({
                                  ...current,
                                  role_ids: Array.from(
                                    e.target.selectedOptions,
                                    (option) => option.value,
                                  ),
                                }))
                              }
                            >
                              {roleCatalog.roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                            <small>
                              可多选，按住 Ctrl／Command 选择多个权限组。
                            </small>
                          </Field>
                        )}
                        <div className="form-actions">
                          <Button
                            variant="secondary"
                            onClick={() => setEditingId("")}
                          >
                            取消
                          </Button>
                          <Button
                            loading={saving}
                            onClick={() => void saveEdit()}
                          >
                            保存档案
                          </Button>
                        </div>
                        <p className="muted small-note">
                          办理离职会同时停用账号并撤销会话；复职后还需单独启用账号。
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty
              icon={Users}
              title="还没有人员档案"
              detail="创建第一个律所账号开始使用。"
            />
          )}
        </section>
      ) : (
        <RolesTab
          actor={actor}
          roles={roleCatalog.roles}
          actions={roleCatalog.actions}
          scopes={roleCatalog.scopes}
          onChanged={load}
        />
      )}
    </div>
  );
}

function RolesTab({
  actor,
  roles: roleList,
  actions,
  scopes,
  onChanged,
}: {
  actor: Actor;
  roles: Role[];
  actions: string[];
  scopes: Record<string, string[]>;
  onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [name, setName] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [selected, setSelected] = useState<Record<string, Scope>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const canManage = can(actor, "role.manage");
  const permissionActions = actions.length
    ? actions
    : Object.keys(scopes).length
      ? Object.keys(scopes)
      : Object.keys(actionLabels);
  const scopeLabels: Record<string, string> = {
    own: "本人记录",
    participating: "参与案件",
    all: "全所",
  };
  function scopesFor(action: string): string[] {
    return scopes[action]?.length
      ? scopes[action]
      : ["own", "participating", "all"];
  }
  function defaultScope(options: string[]): Scope {
    return (
      options.includes("participating")
        ? "participating"
        : (options[0] ?? "own")
    ) as Scope;
  }
  function resetEditor() {
    setShowForm(false);
    setEditingId("");
    setName("");
    setSourceId("");
    setSelected({});
    setError("");
  }
  function beginEdit(role: Role) {
    if (role.builtin || role.preset) return;
    const permissions: Record<string, Scope> = {};
    role.permissions?.forEach((permission) => {
      permissions[permission.action] = permission.scope as Scope;
    });
    setEditingId(role.id);
    setName(role.name);
    setSourceId("");
    setSelected(permissions);
    setError("");
    setShowForm(true);
  }
  function cloneSource(id: string) {
    setSourceId(id);
    const role = roleList.find((item) => item.id === id);
    const permissions: Record<string, Scope> = {};
    role?.permissions?.forEach((permission) => {
      permissions[permission.action] = permission.scope as Scope;
    });
    setSelected(permissions);
  }
  async function saveRole() {
    if (!name.trim()) return setError("请填写权限组名称");
    setSaving(true);
    setError("");
    const payload = {
      name: name.trim(),
      permissions: Object.entries(selected).map(([action, scope]) => ({
        action,
        scope,
      })),
    };
    try {
      if (editingId) await roles.update(editingId, payload);
      else await roles.create(payload);
      resetEditor();
      await onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">ACCESS GROUPS</p>
          <h2>权限组</h2>
          <p className="muted">
            管理员可以复制预设角色，按操作设置数据范围；业务审批规则不能通过权限组关闭。
          </p>
        </div>
        {canManage && (
          <Button
            icon={Plus}
            onClick={() => {
              if (showForm) resetEditor();
              else {
                setEditingId("");
                setName("");
                setSourceId("");
                setSelected({});
                setError("");
                setShowForm(true);
              }
            }}
          >
            {showForm ? "取消编辑" : "新建权限组"}
          </Button>
        )}
      </div>
      {error && <ErrorBox message={error} />}
      {showForm && (
        <div className="role-editor">
          <Field label="权限组名称" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：案件助理"
            />
          </Field>
          {!editingId && (
            <Field label="复制已有角色">
              <select
                value={sourceId}
                onChange={(e) => cloneSource(e.target.value)}
              >
                <option value="">不复制，重新选择</option>
                {roleList.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <div className="permission-grid">
            {permissionActions.map((action) => {
              const options = scopesFor(action);
              const selectedScope = selected[action];
              const value = options.includes(String(selectedScope))
                ? String(selectedScope)
                : options[0];
              return (
                <label className="permission-row" key={action}>
                  <input
                    type="checkbox"
                    checked={Boolean(selectedScope)}
                    onChange={(e) =>
                      setSelected((current) => {
                        const next = { ...current };
                        if (e.target.checked)
                          next[action] = defaultScope(options);
                        else delete next[action];
                        return next;
                      })
                    }
                  />
                  <span>{actionLabels[action] || action}</span>
                  {selectedScope && (
                    <select
                      value={value}
                      onChange={(e) =>
                        setSelected((current) => ({
                          ...current,
                          [action]: e.target.value as Scope,
                        }))
                      }
                    >
                      {options.map((scope) => (
                        <option value={scope} key={scope}>
                          {scopeLabels[scope] || scope}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              );
            })}
          </div>
          <div className="form-actions">
            <Button variant="secondary" onClick={resetEditor}>
              取消
            </Button>
            <Button loading={saving} onClick={() => void saveRole()}>
              {editingId ? "保存修改" : "保存权限组"}
            </Button>
          </div>
        </div>
      )}
      {loadingRoleList(roleList, canManage, beginEdit)}
    </section>
  );
}

function loadingRoleList(
  roleList: Role[],
  canEdit = false,
  onEdit?: (role: Role) => void,
) {
  return roleList.length ? (
    <div className="role-list">
      {roleList.map((role) => (
        <div className="role-row" key={role.id}>
          <div className="role-icon">
            <ShieldCheck size={18} />
          </div>
          <div>
            <strong>{role.name}</strong>
            <span>
              {role.description ||
                (role.builtin || role.preset
                  ? "系统预设角色"
                  : "自定义权限组")}{" "}
              · {role.permissions?.length ?? 0} 项操作权限
            </span>
          </div>
          {(role.builtin || role.preset) && <Badge>预设</Badge>}
          {canEdit && !role.builtin && !role.preset && (
            <Button
              variant="ghost"
              className="small"
              onClick={() => onEdit?.(role)}
            >
              编辑
            </Button>
          )}
          <ChevronDown size={17} />
        </div>
      ))}
    </div>
  ) : (
    <Empty
      icon={ShieldCheck}
      title="还没有权限组"
      detail="系统会提供合伙人、律师、行政财务、系统管理员四个预设角色。"
    />
  );
}

function SystemPage({ actor }: { actor: Actor }) {
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setStatus(await system.backupStatus());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const runs = Array.isArray(status?.runs)
    ? (status.runs as Record<string, unknown>[])
    : [];
  const latest = runs[0];
  const isSuccess = (run?: Record<string, unknown>) =>
    ["completed", "success", "succeeded"].includes(
      String(run?.status ?? "").toLowerCase(),
    );
  const lastSuccess = runs.find((run) => isSuccess(run));
  const latestStatus = latest ? String(latest.status ?? "unknown") : "";
  const latestTone = !latest
    ? "neutral"
    : isSuccess(latest)
      ? "success"
      : /fail|error/i.test(latestStatus)
        ? "danger"
        : "warning";
  return (
    <div className="page">
      <SectionTitle
        eyebrow="SYSTEM"
        title="系统管理"
        description="查看备份状态和运行环境。首期不提供在线升级操作，升级接口只预留给部署流程。"
        actions={
          <Button
            variant="secondary"
            icon={RefreshCw}
            loading={loading}
            onClick={() => void load()}
          >
            刷新
          </Button>
        }
      />
      {error && <ErrorBox message={error} />}
      {loading ? (
        <LoadingRows count={3} />
      ) : (
        <div className="system-grid">
          <section className="panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">BACKUP</p>
                <h2>备份状态</h2>
              </div>
              <Badge
                tone={
                  latestTone as "success" | "warning" | "danger" | "neutral"
                }
              >
                {latest
                  ? isSuccess(latest)
                    ? "最近成功"
                    : labelStatus(latestStatus)
                  : "尚无记录"}
              </Badge>
            </div>
            <dl className="system-list">
              <div>
                <dt>最近一次尝试</dt>
                <dd>
                  {latest
                    ? labelStatus(latestStatus) +
                      " · " +
                      fmtDateTime(
                        String(latest.completed_at ?? latest.started_at ?? "—"),
                      )
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>失败详情</dt>
                <dd>
                  {latest && !isSuccess(latest)
                    ? String(latest.detail ?? "未提供失败原因")
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>上次成功备份</dt>
                <dd>
                  {lastSuccess
                    ? fmtDateTime(
                        String(
                          lastSuccess.completed_at ??
                            lastSuccess.started_at ??
                            "—",
                        ),
                      )
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>备份内容</dt>
                <dd>数据库、附件和关联清单</dd>
              </div>
              <div>
                <dt>保留周期</dt>
                <dd>{String(status?.retention_days ?? 30)} 天</dd>
              </div>
              <div>
                <dt>执行安排</dt>
                <dd>{String(status?.schedule ?? "按部署配置执行")}</dd>
              </div>
            </dl>
          </section>
          <section className="panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">DEPLOYMENT</p>
                <h2>部署信息</h2>
              </div>
              <Building2 size={20} className="muted" />
            </div>
            <div className="deployment-note">
              <ShieldCheck size={20} />
              <div>
                <strong>每家律所独立部署</strong>
                <p>
                  资料保存在当前部署的数据库和附件存储中。访问地址、备份位置和恢复操作由部署管理员保管。
                </p>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function AccountPage({
  actor,
  onLogout,
}: {
  actor: Actor;
  onLogout: () => void;
}) {
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (newPassword.length < 12 || newPassword !== confirm)
      return setError(
        newPassword.length < 12 ? "新密码至少需要 12 位" : "两次新密码不一致",
      );
    try {
      await auth.changePassword(currentPassword, newPassword);
      setDone("密码已修改，其他设备需要重新登录");
      setCurrent("");
      setNew("");
      setConfirm("");
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  return (
    <div className="page narrow-page">
      <SectionTitle
        eyebrow="ACCOUNT"
        title="账号设置"
        description="查看当前登录身份并修改密码。"
      />
      <section className="panel account-card">
        <div className="account-profile">
          <span className="avatar large">
            {actor.name?.slice(0, 1) || "人"}
          </span>
          <div>
            <h2>{actor.name}</h2>
            <p>
              {actor.username} ·{" "}
              {actor.identity === "partner"
                ? "合伙人"
                : actor.identity === "administration"
                  ? "行政财务"
                  : actor.identity === "system"
                    ? "系统管理员"
                    : "律师"}
            </p>
          </div>
        </div>
        <hr />
        <form className="stack" onSubmit={submit}>
          <h3>修改密码</h3>
          <Field label="当前密码" required>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </Field>
          <Field label="新密码" required>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              required
            />
          </Field>
          <Field label="确认新密码" required>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </Field>
          {error && <ErrorBox message={error} />}
          {done && <SuccessBox>{done}</SuccessBox>}
          <div className="form-actions">
            <Button type="submit">保存密码</Button>
            <Button variant="danger" onClick={onLogout} icon={LogOut}>
              退出登录
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}

function NotificationsPage() {
  const [items, setItems] = useState<Notification[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    work
      .notifications()
      .then(setItems)
      .catch((e) => setError(errorMessage(e)));
  }, []);
  async function read(item: Notification) {
    if (!item.read_at) {
      try {
        await work.readNotification(item.id);
        setItems((current) =>
          current.map((entry) =>
            entry.id === item.id
              ? { ...entry, read_at: new Date().toISOString() }
              : entry,
          ),
        );
      } catch (e) {
        setError(errorMessage(e));
      }
    }
  }
  return (
    <div className="page">
      <SectionTitle
        eyebrow="NOTIFICATIONS"
        title="通知"
        description="只展示当前账号有权查看的业务结果。"
      />
      {error && <ErrorBox message={error} />}
      {items.length ? (
        <section className="panel notification-page-list">
          {items.map((item) => (
            <button
              className={`notification-row ${item.read_at ? "" : "unread"}`}
              key={item.id}
              onClick={() => {
                void read(item);
                if (item.matter_id) go(`matter/${item.matter_id}`);
              }}
            >
              <span className="notification-dot-large" />
              <div>
                <strong>{item.title}</strong>
                <span>{item.detail}</span>
                <small>{fmtDateTime(item.created_at)}</small>
              </div>
              <ArrowRight size={16} />
            </button>
          ))}
        </section>
      ) : (
        <section className="panel">
          <Empty icon={Bell} title="还没有通知" />
        </section>
      )}
    </div>
  );
}

export default function App() {
  const [actor, setActor] = useState<Actor | null>(null);
  const [checking, setChecking] = useState(true);
  const [route, setRoute] = useState<Route>(pathRoute());
  useEffect(() => {
    const onHash = () => setRoute(pathRoute());
    window.addEventListener("hashchange", onHash);
    void auth
      .me()
      .then(setActor)
      .catch(() => setActor(null))
      .finally(() => setChecking(false));
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  async function logout() {
    await auth.logout().catch(() => undefined);
    setActor(null);
    go("dashboard");
  }
  if (checking)
    return (
      <div className="app-loading">
        <div className="brand-mark">狮</div>
        <RefreshCw size={19} className="spin" />
        <span>正在连接工作台…</span>
      </div>
    );
  if (!actor) return <Login onAuthenticated={setActor} />;
  if (actor.mustChangePassword)
    return (
      <PasswordGate
        onDone={() => auth.me().then(setActor)}
        onLogout={() => void logout()}
      />
    );
  let view: ReactNode;
  switch (route.name) {
    case "dashboard":
      view = <Dashboard actor={actor} />;
      break;
    case "new-matter":
      view = <MatterEditor actor={actor} />;
      break;
    case "matters":
      view = <MattersList actor={actor} />;
      break;
    case "matter":
      view = route.id ? (
        <MatterDetail actor={actor} matterId={route.id} />
      ) : (
        <MattersList actor={actor} />
      );
      break;
    case "conflict":
      view = <ConflictPage />;
      break;
    case "templates":
      view = <TemplatesPage actor={actor} />;
      break;
    case "staff":
      view = <StaffPage actor={actor} />;
      break;
    case "system":
      view = <SystemPage actor={actor} />;
      break;
    case "account":
      view = <AccountPage actor={actor} onLogout={() => void logout()} />;
      break;
    case "notifications":
      view = <NotificationsPage />;
      break;
    default:
      view = <Dashboard actor={actor} />;
  }
  return (
    <Shell actor={actor} route={route} onLogout={() => void logout()}>
      {view}
    </Shell>
  );
}
